import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import TimelineIcon from '@mui/icons-material/Timeline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CropFreeIcon from '@mui/icons-material/CropFree';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import MouseIcon from '@mui/icons-material/Mouse';
import EditIcon from '@mui/icons-material/Edit';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import apiService from '../services/api';

const AnnotationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '85vh',
  gap: theme.spacing(2),
}));

const ToolsPanel = styled(Box)(({ theme }) => ({
  width: 300,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  overflowY: 'auto',
  maxHeight: '100%',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[200],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[400],
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.grey[500],
    },
  },
  // Override for specific MuiBox-root classes
  '& .MuiBox-root': {
    overflowY: 'auto',
    maxHeight: 'inherit',
  },
  // Specific override for css-waf0qy class
  '& .css-waf0qy': {
    overflowY: 'auto !important',
    maxHeight: '100% !important',
  },
}));

const ImageCanvas = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ToolButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  padding: theme.spacing(1.5),
  border: `2px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: active ? theme.palette.primary.light : 'transparent',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },
}));

const AnnotationImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'portrait' && prop !== 'imageType',
})(({ portrait, imageType }) => {
  const isPortrait = Boolean(portrait);
  const isTapping = String(imageType || '') === 'tapping';

  if (isTapping) {
    return {
      maxWidth: '100%',
      maxHeight: '89%',
      width: isPortrait ? 'auto' : '100%',
      height: isPortrait ? '100%' : 'auto',
      objectFit: 'contain',
      display: 'block',
    };
  }

  return {
    maxWidth: '100%',
    maxHeight: '100%',
    width: isPortrait ? 'auto' : '100%',
    height: isPortrait ? '100%' : 'auto',
    objectFit: 'contain',
    display: 'block',
  };
});

const CanvasOverlay = styled('canvas')({
  position: 'absolute',
  top: 0,
  left: 0,
  cursor: 'crosshair',
});

const ImageAnnotationModal = ({ open, onClose, image, onSave, images = [], currentImageIndex = 0, onNavigateImage, drawingOnly = false, coverAnnotationMode = false, sitePlanImageUrl, sitePlanCandidates = [], onChangeSitePlan, buildingId, imageType = 'drone', showSitePlanPanel = false }) => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [labelText, setLabelText] = useState('');
  const [defects, setDefects] = useState([]);
  

  const [newDefect, setNewDefect] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [annotations, setAnnotations] = useState([]);
  
  // Generate a unique key for each image based on its properties
  const getImageKey = (imageData) => {
    if (!imageData) return null;
    const idPart =
      imageData.id ||
      imageData.file_url ||
      imageData.url ||
      imageData.src ||
      imageData.filename ||
      (imageData.originalImage && (imageData.originalImage.id || imageData.originalImage.url || imageData.originalImage.src)) ||
      'unknown';
    const bPart = buildingId || imageData.building_id || '';
    return `annotations_${String(idPart)}_${String(bPart)}`;
  };

  const normalizePlanUrlKey = (url) => {
    if (!url) return '';
    let value = String(url).trim();
    if (!value) return '';
    try {
      if (/^https?:\/\//i.test(value)) {
        value = new URL(value).pathname;
      }
    } catch {
    }
    value = value.split('#')[0].split('?')[0];
    try {
      value = decodeURIComponent(value);
    } catch {
    }
    if (value.startsWith('uploads/')) return `/${value}`;
    return value;
  };

  const getPlanAnnotationsForUrl = (byPlan, url) => {
    if (!byPlan || !url) return [];
    const candidates = new Set();
    const key = normalizePlanUrlKey(url);
    [url, key].forEach((candidate) => {
      if (!candidate) return;
      candidates.add(candidate);
      candidates.add(candidate.startsWith('/') ? candidate.slice(1) : `/${candidate}`);
    });
    const merged = [];
    const seen = new Set();
    const pushEntries = (entries) => {
      if (!Array.isArray(entries)) return;
      entries.forEach((entry) => {
        const sig = JSON.stringify({
          id: entry?.id ?? null,
          image_id: entry?.image_id ?? null,
          label: entry?.label ?? entry?.text ?? '',
          x: entry?.x ?? null,
          y: entry?.y ?? null,
          width: entry?.width ?? null,
          height: entry?.height ?? null,
          context: entry?.context ?? null,
          coordinate_mode: entry?.coordinate_mode ?? entry?.coordinateMode ?? null,
          render_style: entry?.render_style ?? entry?.renderStyle ?? null,
          suppress_label: entry?.suppress_label ?? entry?.suppressLabel ?? null,
        });
        if (seen.has(sig)) return;
        seen.add(sig);
        merged.push(entry);
      });
    };
    for (const candidate of candidates) {
      pushEntries(byPlan[candidate]);
    }
    const fileName = String(key || url).split('/').pop();
    if (fileName) {
      for (const [savedUrl, entries] of Object.entries(byPlan)) {
        if (Array.isArray(entries) && String(savedUrl).split('?')[0].split('/').pop() === fileName) {
          pushEntries(entries);
        }
      }
    }
    return merged;
  };

  const annotationPlanLinkKey = (ann) => {
    const imageId = ann?.image_id;
    if (imageId == null || imageId === '') return '';
    const context = String(ann?.context || 'drone').trim().toLowerCase();
    return `${String(imageId)}|${context}`;
  };

  const movePlanLinkedAnnotationsToTarget = (byPlan, targetPlanUrl, targetAnnotations = null) => {
    const source = (byPlan && typeof byPlan === 'object') ? byPlan : {};
    const targetPlanKey = normalizePlanUrlKey(targetPlanUrl) || targetPlanUrl;
    if (!targetPlanKey) return source;

    const targetList = Array.isArray(targetAnnotations)
      ? targetAnnotations
      : (Array.isArray(source[targetPlanKey]) ? source[targetPlanKey] : []);
    const movingKeys = new Set(
      targetList
        .map(annotationPlanLinkKey)
        .filter(Boolean)
    );
    if (movingKeys.size === 0) {
      return {
        ...source,
        [targetPlanKey]: targetList,
      };
    }

    const nextByPlan = {};
    Object.entries(source).forEach(([url, anns]) => {
      if (!url || !Array.isArray(anns)) return;
      const normalizedUrl = normalizePlanUrlKey(url) || url;
      if (normalizedUrl === targetPlanKey) return;
      nextByPlan[normalizedUrl] = anns.filter((ann) => !movingKeys.has(annotationPlanLinkKey(ann)));
    });
    nextByPlan[targetPlanKey] = targetList;
    return nextByPlan;
  };

  const getCoverAnnotationsForImageId = (byImageId, imageId, context = imageType) => {
    if (!byImageId || imageId == null) return [];
    const entries = byImageId[String(imageId)];
    if (!Array.isArray(entries)) return [];
    const target = String(context || 'drone').trim().toLowerCase();
    return entries.filter((ann) => String(ann?.context || 'drone').trim().toLowerCase() === target);
  };

  const normalizeDefects = (raw) => {
    const items = Array.isArray(raw) ? raw : [];
    const counts = new Map();
    for (const item of items) {
      let type = '';
      let count = 1;
      if (typeof item === 'string') {
        type = item;
      } else if (item && typeof item === 'object') {
        type = item.type || item.defectType || item.description || item.name || '';
        const c = item.count ?? item.qty ?? item.quantity ?? item.occurrences ?? 1;
        const n = Number(c);
        count = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
      }
      const normalizedType = String(type || '').trim();
      if (!normalizedType) continue;
      const key = normalizedType.toLowerCase();
      const current = counts.get(key) || { type: normalizedType, count: 0 };
      counts.set(key, { ...current, count: current.count + count });
    }
    return Array.from(counts.values());
  };

  const normalizeRecommendationValue = (value) => {
    return String(value || '').replace(/^Level\s+.*?:\s*/i, '').trim();
  };

  const normalizeRecommendationSummary = (values) => {
    const items = (Array.isArray(values) ? values : [values])
      .flatMap((value) => String(value || '').split(';'))
      .map(normalizeRecommendationValue)
      .filter(Boolean);
    const unique = new Map();
    items.forEach((item) => {
      const key = item.toLowerCase();
      if (!unique.has(key)) unique.set(key, item);
    });
    return Array.from(unique.values()).join('; ');
  };

  const expandDefectTypes = (raw) => {
    const normalized = normalizeDefects(raw);
    const out = [];
    for (const d of normalized) {
      for (let i = 0; i < (d.count || 1); i++) out.push(d.type);
    }
    return out;
  };

  const persistAnnotationDataToLocalStorage = (nextDefects, nextRecommendation = recommendation) => {
    if (!image) return;
    const imageKey = getImageKey(image);
    if (!imageKey) return;
    try {
      const savedData = localStorage.getItem(imageKey);
      const existingAnnotations = savedData ? JSON.parse(savedData) : [];
      const annotationData = {
        annotations: existingAnnotations,
        defects: nextDefects,
        recommendation: nextRecommendation,
      };
      localStorage.setItem(`${imageKey}_data`, JSON.stringify(annotationData));
    } catch (error) {
      console.error('Error saving defects/recommendation to localStorage:', error);
    }
  };
  const [showReportConfirmation, setShowReportConfirmation] = useState(false);
  const [reportFormat, setReportFormat] = useState('PDF');
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentEndPoint, setCurrentEndPoint] = useState(null);
  const [isPortrait, setIsPortrait] = useState(false);
  const [sitePlanAnnotationsByPlan, setSitePlanAnnotationsByPlan] = useState({});
  const [coverAnnotationsByImageId, setCoverAnnotationsByImageId] = useState({});
  const [sitePlanCurrentRect, setSitePlanCurrentRect] = useState(null);
  const [isSitePlanDrawing, setIsSitePlanDrawing] = useState(false);
  const [sitePlanAspectRatio, setSitePlanAspectRatio] = useState(1);
  
  // YOLO detection states
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionResults, setDetectionResults] = useState([]);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.5);
  const [blurCars, setBlurCars] = useState(false);
  const [blurredImageUrl, setBlurredImageUrl] = useState(null);
  const [sitePlanIndex, setSitePlanIndex] = useState(0);
  const [annotationSeverity, setAnnotationSeverity] = useState('repair');
  const [isToolsPanelOpen, setIsToolsPanelOpen] = useState(true);
  const [rightPanelWidth, setRightPanelWidth] = useState(360);
  const [isResizingRight, setIsResizingRight] = useState(false);
  const [isSitePlanModalOpen, setIsSitePlanModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSavingSitePlan, setIsSavingSitePlan] = useState(false);
  const [isSavingLevel, setIsSavingLevel] = useState(false);
  const isBuildingPlanAnnotationImage = Boolean(
    image?.source_type === 'building_plan' ||
    image?.sourceType === 'building_plan' ||
    image?.isBuildingPlan ||
    image?.file_url
  );
  const isYoloEligibleImage = Boolean(
    image?.id &&
    !isBuildingPlanAnnotationImage &&
    !String(image?.id || '').startsWith('building_plan_')
  );
  const isCoverElevationAnnotationImage = Boolean(coverAnnotationMode && image?.id);
  const isSimpleDrawingMode = Boolean(drawingOnly || isCoverElevationAnnotationImage);
  const annotationImageSrc = image
    ? (blurredImageUrl || (isBuildingPlanAnnotationImage ? (image.src || image.url) : ((image.id) ? apiService.getImageUrl(image.id) : (image.src || image.url))))
    : '';
  const annotationImageKey = image
    ? [
        image.id || image.file_url || image.url || image.src || image.filename || currentImageIndex,
        imageType || 'drone',
        blurredImageUrl ? 'blurred' : 'original',
      ].join('|')
    : 'empty';
  const [isEditingLevelInline, setIsEditingLevelInline] = useState(false);
  const [autoAdvanceOnSave, setAutoAdvanceOnSave] = useState(() => {
    try {
      const v = localStorage.getItem('autoAdvanceOnSave');
      return v ? JSON.parse(v) : false;
    } catch {
      return false;
    }
  });
  const [selectedSitePlanAnnotationId, setSelectedSitePlanAnnotationId] = useState(null);
  const [sitePlanResizeState, setSitePlanResizeState] = useState(null); // { handle: 'nw'|'ne'|'sw'|'se', startX, startY, startWidth, startHeight, startLeft, startTop }
  const sitePlanAnnotationFontSize = 18;
  const [levelInfo, setLevelInfo] = useState(image?.floor_name || '');
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [isEditingAnnotation, setIsEditingAnnotation] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [annDrag, setAnnDrag] = useState(null); // { mode: 'move'|'nw'|'ne'|'sw'|'se', startNatX, startNatY, annStart: {...} }
  const [imageNumberOffset, setImageNumberOffset] = useState(0);
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const sitePlanContainerRef = useRef(null);
  const sitePlanModalRef = useRef(null);

  

  const recommendationTypes = [
    //'Monitor',
    'Require repair',
    'Unsafe',
    //'Immediate Action Required',
    //'Schedule Maintenance',
    //'Further Investigation'
  ];
  const [defectTypes, setDefectTypes] = useState([]);
  const currentRecommendation = String(recommendation || '').trim();
  const visibleRecommendationTypes = currentRecommendation && !recommendationTypes.includes(currentRecommendation)
    ? [currentRecommendation, ...recommendationTypes]
    : recommendationTypes;

  useEffect(() => {
    const loadDefectTypes = async () => {
      try {
        const resp = await apiService.getDefectTypes();
        const next = (resp?.items || [])
          .map((it) => String(it?.name || '').trim())
          .filter(Boolean);
        setDefectTypes(next);
      } catch (e) {}
    };
    if (open) loadDefectTypes();
  }, [open]);

  const handleRemoveSitePlanAnnotation = async (id) => {
    if (!currentSitePlanUrl) return;
    const currentSitePlanKey = normalizePlanUrlKey(currentSitePlanUrl) || currentSitePlanUrl;
    const existing = getPlanAnnotationsForUrl(sitePlanAnnotationsByPlan, currentSitePlanUrl);
    const updatedForPlan = existing.filter((ann) => ann.id !== id);
    const updatedByPlan = {
      ...sitePlanAnnotationsByPlan,
      [currentSitePlanKey]: updatedForPlan,
    };
    setSitePlanAnnotationsByPlan(updatedByPlan);
    await persistSitePlanAnnotations(updatedByPlan);
  };

  const sitePlanCoordinatesFromEvent = (e) => {
    // Support interaction from both sidebar and modal by using currentTarget
    const container = e.currentTarget || sitePlanContainerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return {
      x: Math.min(Math.max(x, 0), 1),
      y: Math.min(Math.max(y, 0), 1),
    };
  };

  const startResizingRight = React.useCallback(() => {
    setIsResizingRight(true);
  }, []);

  const stopResizingRight = React.useCallback(() => {
    setIsResizingRight(false);
  }, []);

  const handleResizeRight = React.useCallback((e) => {
    if (isResizingRight) {
      // Calculate new width based on window width and mouse position
      // The right panel is on the right, so width is windowWidth - mouseX
      // But we are in a modal/dialog, so we should consider the dialog layout.
      // However, simplified approach: window.innerWidth - e.clientX
      // This assumes the dialog is full width or close to it.
      // A better approach might be to calculate delta.
      
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth > 250 && newWidth < 900) {
        setRightPanelWidth(newWidth);
      }
    }
  }, [isResizingRight]);

  useEffect(() => {
    if (isResizingRight) {
      document.addEventListener('mousemove', handleResizeRight);
      document.addEventListener('mouseup', stopResizingRight);
    } else {
      document.removeEventListener('mousemove', handleResizeRight);
      document.removeEventListener('mouseup', stopResizingRight);
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeRight);
      document.removeEventListener('mouseup', stopResizingRight);
    };
  }, [isResizingRight, handleResizeRight, stopResizingRight]);

  const handleSaveSitePlan = async () => {
    if (!buildingId) return;
    setIsSavingSitePlan(true);
    try {
      await persistSitePlanAnnotations();
      setSnackbarMessage('Site plan annotations saved successfully');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving site plan annotations:', error);
      setSnackbarMessage('Failed to save site plan annotations');
      setSnackbarOpen(true);
    } finally {
      setIsSavingSitePlan(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleDeleteSitePlanAnnotation = (id) => {
    if (!currentSitePlanUrl) return;
    const currentSitePlanKey = normalizePlanUrlKey(currentSitePlanUrl) || currentSitePlanUrl;
    const existing = getPlanAnnotationsForUrl(sitePlanAnnotationsByPlan, currentSitePlanUrl);
    const filtered = existing.filter(a => !(a.id === id && (a.context || 'drone') === imageType));
    const updated = { ...sitePlanAnnotationsByPlan, [currentSitePlanKey]: filtered };
    setSitePlanAnnotationsByPlan(updated);
    persistSitePlanAnnotations(updated);
    if (selectedSitePlanAnnotationId === id) setSelectedSitePlanAnnotationId(null);
  };

  const handleSitePlanMouseDown = (e) => {
    if (sitePlanResizeState) return;
    if (!images || images.length === 0 || !currentSitePlanUrl) return;
    
    // Force expanding to full-size modal before annotating
    if (!isSitePlanModalOpen) {
      setSnackbarMessage('Expand site plan to annotate. Click the fullscreen icon.');
      setSnackbarOpen(true);
      return;
    }
    // Block while resizing the right panel to avoid accidental draws
    if (isResizingRight) {
      setSnackbarMessage('Finish resizing or expand site plan to annotate.');
      setSnackbarOpen(true);
      return;
    }
    // Block annotation if current image has no annotations
    if (annotations.length === 0) {
      setSnackbarMessage('Please annotate the image first before adding to site plan.');
      setSnackbarOpen(true);
      return;
    }

    const coords = sitePlanCoordinatesFromEvent(e);
    if (!coords) return;
    
    const boxWidth = 0.055;
    const boxHeight = 0.045;
    const clampedX = Math.min(Math.max(coords.x - boxWidth / 2, 0), 1 - boxWidth);
    const clampedY = Math.min(Math.max(coords.y - boxHeight / 2, 0), 1 - boxHeight);
    
    const stableImageId = image?.id != null ? String(image.id) : String(currentImageIndex + 1);
    const computedLabel = photoNoByImageId.get(stableImageId);
    const id = stableImageId;
    const label = String(computedLabel || currentPhotoNumber);
    const currentSitePlanKey = normalizePlanUrlKey(currentSitePlanUrl) || currentSitePlanUrl;
    const existingForPlan = getPlanAnnotationsForUrl(sitePlanAnnotationsByPlan, currentSitePlanUrl);
    const placedBox = findNonOverlappingSitePlanPosition(
      clampedX,
      clampedY,
      boxWidth,
      boxHeight,
      existingForPlan.filter((ann) => (ann.context || 'drone') === imageType),
      id
    );
    const existingIndex = existingForPlan.findIndex((ann) => {
      const annKey = ann?.image_id != null ? String(ann.image_id) : (ann?.id != null ? String(ann.id) : String(ann.label || ''));
      return annKey === String(id) && (ann.context || 'drone') === imageType;
    });
    
    let updatedForPlan;
    const severity = annotationSeverity;
    
    if (existingIndex >= 0) {
      updatedForPlan = [...existingForPlan];
      updatedForPlan[existingIndex] = {
        ...updatedForPlan[existingIndex],
        x: placedBox.x,
        y: placedBox.y,
        width: boxWidth,
        height: boxHeight,
        label,
        severity, 
        context: imageType, 
        image_id: stableImageId,
        level: levelInfo || updatedForPlan[existingIndex]?.level || '',
      };
    } else {
      updatedForPlan = [
        ...existingForPlan,
        {
          id,
          x: placedBox.x,
          y: placedBox.y,
          width: boxWidth,
          height: boxHeight,
          label,
          severity, 
          context: imageType, 
          image_id: stableImageId,
          level: levelInfo || '',
        },
      ];
    }
    const updatedByPlan = movePlanLinkedAnnotationsToTarget(
      {
        ...sitePlanAnnotationsByPlan,
        [currentSitePlanKey]: updatedForPlan,
      },
      currentSitePlanKey,
      updatedForPlan
    );
    setSitePlanAnnotationsByPlan(updatedByPlan);
    persistSitePlanAnnotations(updatedByPlan);
    setSelectedSitePlanAnnotationId(id);
  };

  const sitePlanAnnotationsByPlanRef = useRef(sitePlanAnnotationsByPlan);
  sitePlanAnnotationsByPlanRef.current = sitePlanAnnotationsByPlan;
  const sitePlanDragMovedRef = useRef(false);

  const handleSitePlanMouseMove = (e) => {
    if (!sitePlanResizeState) return;
    e.preventDefault();
    
    const container = isSitePlanModalOpen ? sitePlanModalRef.current : sitePlanContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    
    const deltaX = (e.clientX - sitePlanResizeState.startX) / rect.width;
    const deltaY = (e.clientY - sitePlanResizeState.startY) / rect.height;
    
    const { handle, annId, startLeft, startTop, startWidth, startHeight } = sitePlanResizeState;
    
    let newX = startLeft;
    let newY = startTop;
    let newWidth = startWidth;
    let newHeight = startHeight;
    
    if (handle === 'move') {
        newX = Math.max(0, Math.min(startLeft + deltaX, 1 - startWidth));
        newY = Math.max(0, Math.min(startTop + deltaY, 1 - startHeight));
    }
    else if (handle.includes('e')) {
        newWidth = Math.max(0.01, startWidth + deltaX);
    }
    if (handle && handle !== 'move' && handle.includes('w')) {
        newWidth = Math.max(0.01, startWidth - deltaX);
        newX = startLeft + (startWidth - newWidth);
    }
    if (handle && handle !== 'move' && handle.includes('s')) {
        newHeight = Math.max(0.01, startHeight + deltaY);
    }
    if (handle && handle !== 'move' && handle.includes('n')) {
        newHeight = Math.max(0.01, startHeight - deltaY);
        newY = startTop + (startHeight - newHeight);
    }
    
    // Annotations are stored under the normalized plan key, so resolve via the same helper used elsewhere
    const currentSitePlanKey = normalizePlanUrlKey(currentSitePlanUrl) || currentSitePlanUrl;
    const prevByPlan = sitePlanAnnotationsByPlanRef.current;
    const existingForPlan = getPlanAnnotationsForUrl(prevByPlan, currentSitePlanUrl);
    const annIndex = existingForPlan.findIndex(a => String(a.id) === String(annId) && (a.context || 'drone') === imageType);
    if (annIndex === -1) return;
    
    const updatedForPlan = [...existingForPlan];
    updatedForPlan[annIndex] = {
        ...updatedForPlan[annIndex],
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight
    };
    
    const updatedByPlan = movePlanLinkedAnnotationsToTarget(
      { ...prevByPlan, [currentSitePlanKey]: updatedForPlan },
      currentSitePlanKey,
      updatedForPlan
    );
    sitePlanDragMovedRef.current = true;
    sitePlanAnnotationsByPlanRef.current = updatedByPlan;
    setSitePlanAnnotationsByPlan(updatedByPlan);
  };

  const handleSitePlanMouseUp = () => {
    if (sitePlanResizeState) {
        if (sitePlanDragMovedRef.current) {
          persistSitePlanAnnotations(sitePlanAnnotationsByPlanRef.current);
        }
        sitePlanDragMovedRef.current = false;
        setSitePlanResizeState(null);
     }
   };

  // Track drag/resize on the window so the box keeps following the cursor even if it leaves the plan
  useEffect(() => {
    if (!sitePlanResizeState) return undefined;
    window.addEventListener('mousemove', handleSitePlanMouseMove);
    window.addEventListener('mouseup', handleSitePlanMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleSitePlanMouseMove);
      window.removeEventListener('mouseup', handleSitePlanMouseUp);
    };
  }, [sitePlanResizeState]);

   const handleResizeStart = (e, handle, ann) => {
    e.stopPropagation();
    e.preventDefault();
    setSitePlanResizeState({
        handle,
        annId: ann.id,
        startX: e.clientX,
        startY: e.clientY,
        startLeft: ann.x,
        startTop: ann.y,
        startWidth: ann.width,
        startHeight: ann.height
    });
  };

  useEffect(() => {
    if (!open) return;
    if (!sitePlanCandidates || sitePlanCandidates.length === 0) {
      setSitePlanIndex(0);
      return;
    }
    if (sitePlanImageUrl) {
      const idx = sitePlanCandidates.indexOf(sitePlanImageUrl);
      setSitePlanIndex(idx >= 0 ? idx : 0);
    } else {
      setSitePlanIndex(0);
    }
  }, [open, sitePlanCandidates, sitePlanImageUrl]);

  const currentSitePlanUrl =
    sitePlanCandidates && sitePlanCandidates.length > 0
      ? sitePlanCandidates[Math.min(Math.max(sitePlanIndex, 0), sitePlanCandidates.length - 1)]
      : null;

  useEffect(() => {
    let cancelled = false;
    const loadImageNumberOffset = async () => {
      if (!open || String(imageType || 'drone') !== 'tapping' || !buildingId) {
        setImageNumberOffset(0);
        return;
      }
      try {
        const response = await apiService.getBuildingImages(buildingId, 'drone');
        const folders = response?.folder_structure || {};
        const count = Object.values(folders).reduce((total, list) => total + (Array.isArray(list) ? list.length : 0), 0);
        if (!cancelled) setImageNumberOffset(count);
      } catch (e) {
        console.error('Failed to load drone image count for tapping numbering:', e);
        if (!cancelled) setImageNumberOffset(0);
      }
    };
    loadImageNumberOffset();
    return () => {
      cancelled = true;
    };
  }, [open, imageType, buildingId]);

  const photoNoByImageId = React.useMemo(() => {
    const map = new Map();
    const list = Array.isArray(images) ? images : [];
    list.forEach((img, idx) => {
      const id = img?.id != null ? String(img.id) : null;
      if (id) map.set(id, imageNumberOffset + idx + 1);
    });
    return map;
  }, [images, imageNumberOffset]);

  const currentPhotoNumber = imageNumberOffset + currentImageIndex + 1;
  const totalPhotoNumber = imageNumberOffset + images.length;

  const resolveSitePlanLabel = React.useCallback((ann) => {
    const imgId = ann?.image_id != null ? String(ann.image_id) : (ann?.id != null ? String(ann.id) : '');
    const n = imgId ? photoNoByImageId.get(imgId) : null;
    if (n) return String(n);
    if (ann?.label != null) return String(ann.label);
    if (ann?.id != null) return String(ann.id);
    return '';
  }, [photoNoByImageId]);

  const currentSitePlanAnnotations =
    currentSitePlanUrl
      ? getPlanAnnotationsForUrl(sitePlanAnnotationsByPlan, currentSitePlanUrl).filter(ann => (ann.context || 'drone') === imageType)
      : [];

  useEffect(() => {
    if (!currentSitePlanUrl) {
      setSitePlanAspectRatio(1);
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setSitePlanAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = currentSitePlanUrl;
  }, [currentSitePlanUrl]);

  const sitePlanAnnotations = currentSitePlanAnnotations;

  const isLineLikeAnnotation = (annotation) => annotation && (annotation.type === 'line' || annotation.type === 'arrow');
  // Boxes/lines carry their own defect list in `annotation.defects`
  const isDefectBoxAnnotation = (annotation) => Boolean(annotation && (annotation.type === 'square' || isLineLikeAnnotation(annotation)));
  const getAnnotationDefects = (annotation) => (Array.isArray(annotation?.defects) ? annotation.defects : [])
    .map((d) => String(d || '').trim())
    .filter(Boolean);
  const defectBoxes = annotations.filter(isDefectBoxAnnotation);
  const hasMultipleDefectBoxes = defectBoxes.length > 1;
  // New defects go to the selected box, otherwise to the most recently drawn box
  const targetDefectBox = defectBoxes.find((a) => a.id === selectedAnnotationId) || defectBoxes[defectBoxes.length - 1] || null;
  const targetDefectBoxNumber = targetDefectBox ? defectBoxes.indexOf(targetDefectBox) + 1 : 0;
  // Image-level defects plus every box's defects (count = number of occurrences)
  const allDefects = normalizeDefects([...expandDefectTypes(defects), ...defectBoxes.flatMap(getAnnotationDefects)]);

  const drawCanvasArrow = (ctx, x1, y1, x2, y2, options = {}) => {
    const color = options.color || '#F44336';
    const lineWidth = options.lineWidth || 2;
    const dashed = Boolean(options.dashed);
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const headLength = Math.max(7, Math.min(16, Math.hypot(x2 - x1, y2 - y1) * 0.11));

    const drawHead = (tipX, tipY, headAngle) => {
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      ctx.lineTo(tipX - headLength * Math.cos(headAngle - Math.PI / 6), tipY - headLength * Math.sin(headAngle - Math.PI / 6));
      ctx.lineTo(tipX - headLength * Math.cos(headAngle + Math.PI / 6), tipY - headLength * Math.sin(headAngle + Math.PI / 6));
      ctx.closePath();
      ctx.fill();
    };

    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (dashed) ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    if (dashed) ctx.setLineDash([]);
    drawHead(x2, y2, angle);
    drawHead(x1, y1, angle + Math.PI);
    ctx.restore();
  };

  const canPrevSitePlan = sitePlanCandidates && sitePlanCandidates.length > 1 && sitePlanIndex > 0;
  const canNextSitePlan =
    sitePlanCandidates && sitePlanCandidates.length > 1 && sitePlanIndex < sitePlanCandidates.length - 1;

  const handlePrevSitePlan = () => {
    if (!sitePlanCandidates || sitePlanCandidates.length === 0) return;
    const newIndex = canPrevSitePlan ? Math.max(sitePlanIndex - 1, 0) : sitePlanIndex;
    if (newIndex === sitePlanIndex) return;
    setSitePlanIndex(newIndex);
    if (onChangeSitePlan) {
      const clampedIndex = Math.min(Math.max(newIndex, 0), sitePlanCandidates.length - 1);
      const nextUrl = sitePlanCandidates[clampedIndex];
      if (nextUrl) {
        onChangeSitePlan(nextUrl);
      }
    }
  };

  const handleNextSitePlan = () => {
    if (!sitePlanCandidates || sitePlanCandidates.length === 0) return;
    const newIndex = canNextSitePlan
      ? Math.min(sitePlanIndex + 1, sitePlanCandidates.length - 1)
      : sitePlanIndex;
    if (newIndex === sitePlanIndex) return;
    setSitePlanIndex(newIndex);
    if (onChangeSitePlan) {
      const clampedIndex = Math.min(Math.max(newIndex, 0), sitePlanCandidates.length - 1);
      const nextUrl = sitePlanCandidates[clampedIndex];
      if (nextUrl) {
        onChangeSitePlan(nextUrl);
      }
    }
  };

  useEffect(() => {
    if (!open || !buildingId) return;
    const loadSitePlanAnnotations = async () => {
      try {
        const result = await apiService.getSitePlanAnnotations(buildingId);
        if (result && result.by_plan_url) {
          const mapped = {};
          Object.entries(result.by_plan_url).forEach(([url, entries]) => {
            if (Array.isArray(entries)) {
              mapped[url] = entries.map((entry, index) => {
                const rawId = entry?.image_id != null ? String(entry.image_id) : (entry?.id != null ? String(entry.id) : String(index + 1));
                return {
                  id: rawId,
                  image_id: entry?.image_id === null
                    ? null
                    : (entry?.image_id != null ? String(entry.image_id) : null),
                  x: entry.x,
                  y: entry.y,
                  width: entry.width,
                  height: entry.height,
                  startX: entry.startX ?? entry.start_x,
                  startY: entry.startY ?? entry.start_y,
                  endX: entry.endX ?? entry.end_x,
                  endY: entry.endY ?? entry.end_y,
                  label: entry.label != null ? String(entry.label) : String(index + 1),
                  severity: entry.severity || 'repair',
                  context: entry.context || 'drone',
                  level: entry.level || '',
                  type: entry.type || 'square',
                  coordinate_mode: entry.coordinate_mode || entry.coordinateMode || '',
                  render_style: entry.render_style || entry.renderStyle || '',
                  suppress_label: Boolean(entry.suppress_label || entry.suppressLabel),
                };
              });
            }
          });
          setSitePlanAnnotationsByPlan(mapped);
        }
        if (result && result.cover_annotations_by_image_id && typeof result.cover_annotations_by_image_id === 'object') {
          setCoverAnnotationsByImageId(result.cover_annotations_by_image_id);
        } else {
          setCoverAnnotationsByImageId({});
        }
      } catch (e) {
        console.error('Error loading site plan annotations:', e);
      }
    };
    loadSitePlanAnnotations();
  }, [open, buildingId]);

  // Load annotations and data from database first, then fallback to localStorage when modal opens with an image
  useEffect(() => {
    let cancelled = false;
    const loadAnnotationData = async () => {
      if (open && image) {
        console.log('🔍 ANNOTATION DEBUG - Modal opened with image:', {
          imageId: image.id,
          imageSrc: image.src,
          imageFilename: image.filename,
          imageFile: image.file?.name,
          fullImageObject: image
        });
        
        const imageKey = getImageKey(image);
        console.log('🔍 ANNOTATION DEBUG - Generated image key:', imageKey);

        if (isCoverElevationAnnotationImage) {
          const savedCoverAnnotations = getCoverAnnotationsForImageId(coverAnnotationsByImageId, image.id, imageType);
          const mappedCoverAnnotations = savedCoverAnnotations
            .filter((ann) => ann && (ann.type || 'square') === 'square')
            .map((ann, idx) => ({
              type: 'square',
              x: ann.x,
              y: ann.y,
              width: ann.width,
              height: ann.height,
              text: '',
              severity: ann.severity || 'repair',
              context: ann.context || imageType,
              coordinate_mode: ann.coordinate_mode || ann.coordinateMode || 'pixel',
              render_style: ann.render_style || ann.renderStyle || 'outline',
              suppress_label: Boolean(ann.suppress_label || ann.suppressLabel),
              id: ann.id || `cover_ann_${idx}_${Date.now()}`
            }));

          let localAnnotations = [];
          if (imageKey) {
            try {
              const savedAnnotations = localStorage.getItem(imageKey);
              if (savedAnnotations) {
                localAnnotations = (JSON.parse(savedAnnotations) || []).filter((ann) => ann && ann.type === 'square');
              }
            } catch (error) {
              console.error('Error loading cover annotations from localStorage:', error);
            }
          }

          const mergedCoverAnnotations = [...mappedCoverAnnotations];
          localAnnotations.forEach((localAnnotation) => {
            const exists = mergedCoverAnnotations.some((savedAnnotation) =>
              Math.abs((savedAnnotation.x || 0) - (localAnnotation.x || 0)) < 5 &&
              Math.abs((savedAnnotation.y || 0) - (localAnnotation.y || 0)) < 5 &&
              Math.abs((savedAnnotation.width || 0) - (localAnnotation.width || 0)) < 5 &&
              Math.abs((savedAnnotation.height || 0) - (localAnnotation.height || 0)) < 5
            );
            if (!exists) mergedCoverAnnotations.push(localAnnotation);
          });

          if (!cancelled) {
            setDefects([]);
            setRecommendation('');
            setAnnotations(mergedCoverAnnotations);
            setBlurredImageUrl(null);
            setDetectionResults([]);
            setBlurCars(false);
          }
          return;
        }
        
        let dbDefects = [];
        let dbRecommendation = '';
        let dbAnnotations = [];
        let loadedFromDatabase = false;
        let loadedDefectRows = false;
        
        // First, try to load from database if image has an ID
        if (isYoloEligibleImage) {
          try {
            console.log('🔍 ANNOTATION DEBUG - Loading from database for image ID:', image.id);
            const defectsResponse = await apiService.getImageDefects(image.id);
            
            if (defectsResponse && defectsResponse.defects && defectsResponse.defects.length > 0) {
              console.log('🔍 ANNOTATION DEBUG - Database defects loaded for image ID', image.id, ':', defectsResponse.defects);
              
              dbRecommendation = defectsResponse.defects
                .map((defect) => defect?.recommendation || defect?.recommendationText)
                .reduce((summary, value) => normalizeRecommendationSummary([summary, value]), '');
              
              // Rows sharing the same coordinates belong to the same box; rows without a box stay image-level
              const unassignedTypes = [];
              const boxesByCoords = new Map();
              defectsResponse.defects.forEach((defect) => {
                const type = String(defect?.defect_type || defect?.defectType || defect?.type || defect?.text || '').trim();
                const width = Number(defect?.width) || 0;
                const height = Number(defect?.height) || 0;
                if (defect?.x === undefined || defect?.y === undefined || (width <= 0 && height <= 0)) {
                  if (type) unassignedTypes.push(type);
                  return;
                }
                const key = [defect.x, defect.y, width, height].map((v) => Math.round(Number(v) || 0)).join(':');
                if (!boxesByCoords.has(key)) {
                  boxesByCoords.set(key, {
                    type: 'square',
                    x: Number(defect.x) || 0,
                    y: Number(defect.y) || 0,
                    width,
                    height,
                    text: '',
                    defects: [],
                    id: defect.id || Date.now()
                  });
                }
                const box = boxesByCoords.get(key);
                if (type && !box.defects.some((d) => d.toLowerCase() === type.toLowerCase())) {
                  box.defects.push(type);
                }
              });
              dbDefects = normalizeDefects(unassignedTypes);
              dbAnnotations = Array.from(boxesByCoords.values());
              loadedDefectRows = true;
              
              loadedFromDatabase = true;
              
              console.log('🔍 ANNOTATION DEBUG - Successfully loaded from database for image ID', image.id, ':', {
                defects: dbDefects,
                recommendation: dbRecommendation,
                annotations: dbAnnotations
              });
            } else {
              console.log('🔍 ANNOTATION DEBUG - No defects found in database for image ID:', image.id);
            }
          } catch (error) {
            console.error('Error loading data from database:', error);
            // Continue to localStorage fallback
          }
        }

        // Check sitePlanAnnotationsByPlan for this image URL (for 1.2 plans)
        const url = image.file_url || image.url || image.src;
        const planAnns = getPlanAnnotationsForUrl(sitePlanAnnotationsByPlan, url);
        if (url && planAnns.length > 0) {
            console.log('🔍 ANNOTATION DEBUG - Found site plan annotations for URL:', url);
            const mappedPlanAnns = planAnns.map((ann, idx) => ({
                type: ann.type || 'square',
                x: ann.x,
                y: ann.y,
                width: ann.width,
                height: ann.height,
                startX: ann.startX ?? ann.start_x,
                startY: ann.startY ?? ann.start_y,
                endX: ann.endX ?? ann.end_x,
                endY: ann.endY ?? ann.end_y,
                text: ann.label || '',
                severity: ann.severity || 'repair',
                context: ann.context || imageType,
                coordinate_mode: ann.coordinate_mode || ann.coordinateMode || 'pixel',
                render_style: ann.render_style || ann.renderStyle || 'outline',
                suppress_label: Boolean(ann.suppress_label || ann.suppressLabel),
                id: ann.id || `plan_ann_${idx}_${Date.now()}`
            }));
            
            if (mappedPlanAnns.length > 0) {
                // Merge with existing DB annotations (if any)
                dbAnnotations = [...dbAnnotations, ...mappedPlanAnns];
                loadedFromDatabase = true;
            }
        }
        
        // Load from localStorage
        let localDefects = [];
        let localRecommendation = '';
        let localAnnotations = [];
        
        if (imageKey) {
          try {
            console.log('🔍 ANNOTATION DEBUG - Loading from localStorage for key:', imageKey);
            
            // Load drawing annotations
            const savedAnnotations = localStorage.getItem(imageKey);
            if (savedAnnotations) {
              const parsedAnnotations = JSON.parse(savedAnnotations);
              console.log('🔍 ANNOTATION DEBUG - Loading annotations from localStorage:', parsedAnnotations);
              localAnnotations = parsedAnnotations;
            } else {
              console.log('🔍 ANNOTATION DEBUG - No annotations found in localStorage for key:', imageKey);
            }
            
            // Load defects and recommendations
            const savedData = localStorage.getItem(`${imageKey}_data`);
            if (savedData) {
              const parsedData = JSON.parse(savedData);
              console.log('🔍 ANNOTATION DEBUG - Loading annotation data from localStorage:', parsedData);
              if (parsedData.defects) {
                localDefects = normalizeDefects(parsedData.defects);
              }
              if (parsedData.recommendation) {
                localRecommendation = normalizeRecommendationSummary(parsedData.recommendation);
              }
            } else {
              console.log('🔍 ANNOTATION DEBUG - No annotation data found in localStorage for key:', `${imageKey}_data`);
            }
          } catch (error) {
            console.error('Error loading data from localStorage:', error);
          }
        }
        
        const mergedDefects = loadedDefectRows ? dbDefects : localDefects;
        
        const mergedRecommendation = normalizeRecommendationSummary(dbRecommendation || localRecommendation);
        
        // Merge annotations - combine database and localStorage annotations
        const mergedAnnotations = [...dbAnnotations];
        localAnnotations.forEach(localAnnotation => {
          // Check if this annotation already exists (by position and size)
          const exists = mergedAnnotations.some(dbAnnotation => 
            Math.abs(dbAnnotation.x - localAnnotation.x) < 5 &&
            Math.abs(dbAnnotation.y - localAnnotation.y) < 5 &&
            Math.abs((dbAnnotation.width || 50) - (localAnnotation.width || 50)) < 5 &&
            Math.abs((dbAnnotation.height || 50) - (localAnnotation.height || 50)) < 5
          );
          
          if (!exists) {
            mergedAnnotations.push(localAnnotation);
          }
        });
        
        if (cancelled) return;

        // Set the merged data
        setDefects(mergedDefects);
        setRecommendation(mergedRecommendation);
        setAnnotations(mergedAnnotations);
        
        // Reset blur-related states when switching to a new image
        setBlurredImageUrl(null);
        setDetectionResults([]);
        setBlurCars(false);
        
        console.log('🔍 ANNOTATION DEBUG - Final merged data for image ID', image.id, ':', {
          defects: mergedDefects,
          recommendation: mergedRecommendation,
          annotations: mergedAnnotations,
          sources: {
            database: loadedFromDatabase,
            localStorage: localDefects.length > 0 || localRecommendation || localAnnotations.length > 0
          },
          imageKey
        });

        // Trigger automatic YOLO detection for cars and persons when image opens
        if (isYoloEligibleImage && mergedAnnotations.length === 0) {
          console.log('🤖 AUTO-DETECT - Triggering automatic YOLO detection for cars and persons');
          try {
            const result = await apiService.detectObjects(
              image.id,
              0.5, // confidence threshold
              true, // auto save
              false, // don't blur cars
              true // auto detect flag
            );
            
            if (result && result.detections && result.detections.length > 0) {
              console.log('🤖 AUTO-DETECT - YOLO detection completed:', result.detections);
              
              // Convert YOLO detections to annotations
              const yoloAnnotations = result.detections.map((detection, index) => ({
                type: 'square',
                x: detection.x,
                y: detection.y,
                width: detection.width,
                height: detection.height,
                text: `${detection.class_name} (${Math.round(detection.confidence * 100)}%)`,
                context: imageType,
                id: `yolo_${Date.now()}_${index}`
              }));
              
              // Add YOLO annotations to existing annotations
              if (!cancelled) {
                setAnnotations(prev => [...prev, ...yoloAnnotations]);
              }
              
              console.log('🤖 AUTO-DETECT - Added YOLO annotations:', yoloAnnotations);
            } else {
              console.log('🤖 AUTO-DETECT - No objects detected');
            }
          } catch (error) {
            console.error('🤖 AUTO-DETECT - Error during automatic detection:', error);
          }
        }
      }
    };
    
    loadAnnotationData();
    return () => {
      cancelled = true;
    };
  }, [open, image, sitePlanAnnotationsByPlan, coverAnnotationsByImageId, isCoverElevationAnnotationImage, imageType, isYoloEligibleImage]);

  useEffect(() => {
    if (open && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const image = imageRef.current;
      const container = containerRef.current;
      
      const resizeCanvas = () => {
        // Get the actual displayed dimensions of the image
        const imageRect = image.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // Set canvas size to match the displayed image size
        canvas.width = imageRect.width;
        canvas.height = imageRect.height;
        
        // Position the canvas to overlay the image exactly
        canvas.style.width = `${imageRect.width}px`;
        canvas.style.height = `${imageRect.height}px`;
        canvas.style.left = `${imageRect.left - containerRect.left}px`;
        canvas.style.top = `${imageRect.top - containerRect.top}px`;
        
        redrawAnnotations();
      };
      
      // Use a timeout to ensure the image is fully loaded and rendered
      const handleImageLoad = () => {
        setIsPortrait((image.naturalHeight || 0) > (image.naturalWidth || 0));
        setTimeout(resizeCanvas, 100);
      };
      
      image.onload = handleImageLoad;
      window.addEventListener('resize', resizeCanvas);
      
      // Also resize when the image is already loaded
      if (image.complete) {
        handleImageLoad();
      }
      
      return () => {
        window.removeEventListener('resize', resizeCanvas);
      };
    }
  }, [open, annotations, scale, offset, isDrawing, startPoint, currentEndPoint]);

  // Separate useEffect to handle real-time drawing updates
  useEffect(() => {
    console.log('Real-time drawing useEffect triggered:', {
      isDrawing,
      startPoint,
      currentEndPoint,
      shouldRedraw: isDrawing && startPoint && currentEndPoint
    });
    if (isDrawing && startPoint && currentEndPoint) {
      console.log('Triggering redraw for real-time drawing');
      redrawAnnotations();
    }
  }, [isDrawing, startPoint, currentEndPoint]);

  // useEffect to redraw when annotations change and save to localStorage
  useEffect(() => {
    console.log('Annotations changed, triggering redraw:', annotations);
    redrawAnnotations();
    
    // Save annotations to localStorage
    if (image && annotations.length >= 0) {
      const imageKey = getImageKey(image);
      if (imageKey) {
        try {
          localStorage.setItem(imageKey, JSON.stringify(annotations));
          console.log('Annotations saved to localStorage for key:', imageKey);
        } catch (error) {
          console.error('Error saving annotations to localStorage:', error);
        }
      }
    }
  }, [annotations, image]);

  // Redraw so the selected box (and its label colour) updates when selection changes from the defect list
  useEffect(() => {
    redrawAnnotations();
  }, [selectedAnnotationId]);

  // Keep the box that new defects go to visible in the scrollable defect list
  const defectBoxListRef = useRef(null);
  const targetDefectBoxId = targetDefectBox ? targetDefectBox.id : null;
  const targetDefectBoxDefectCount = getAnnotationDefects(targetDefectBox).length;
  useEffect(() => {
    const list = defectBoxListRef.current;
    if (!list || targetDefectBoxId == null) return;
    const card = Array.from(list.children).find((el) => el.dataset.defectBoxId === String(targetDefectBoxId));
    if (!card) return;
    list.scrollTo({ top: Math.max(card.offsetTop - 4, 0), behavior: 'smooth' });
  }, [targetDefectBoxId, targetDefectBoxDefectCount, defectBoxes.length]);

  // When switching images, clear drawing state and load stored annotations/defects
  useEffect(() => {
    if (!image) return;
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentEndPoint(null);
    setSelectedTool(null);
    setScale(1);
    setOffset({ x: 0, y: 0 });
    setAnnotations([]);
    setTimeout(() => redrawAnnotations(), 0);
  }, [image?.id]);

  const redrawAnnotations = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;
    
    console.log('redrawAnnotations called with:', {
      annotationsCount: annotations.length,
      annotations: annotations,
      isDrawing,
      startPoint,
      currentEndPoint
    });
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Get the scaling factors between the original image and displayed image
    const imageRect = image.getBoundingClientRect();
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    const scaleX = imageRect.width / naturalWidth;
    const scaleY = imageRect.height / naturalHeight;
    
    console.log('Image scaling factors:', {
      naturalWidth,
      naturalHeight,
      displayedWidth: imageRect.width,
      displayedHeight: imageRect.height,
      scaleX,
      scaleY
    });
    
    // Don't draw the image on canvas - let the HTML img element show through
    
    // Helper to get color based on imageType
    const getAnnotationColor = () => {
      switch (imageType) {
        case 'overview':
          return '#00BCD4'; // Cyan
        case 'close-range':
          return '#F44336'; // Red
        default:
          return '#F44336'; // Default Red
      }
    };

    annotations.forEach((annotation, index) => {
      console.log(`Drawing annotation ${index}:`, annotation);
      if (annotation.type === 'square' && annotation.width && annotation.height) {
        const scaledX = annotation.x * scaleX;
        const scaledY = annotation.y * scaleY;
        const scaledWidth = annotation.width * scaleX;
        const scaledHeight = annotation.height * scaleY;
        
        console.log(`Scaled coordinates for annotation ${index}:`, {
          original: { x: annotation.x, y: annotation.y, width: annotation.width, height: annotation.height },
          scaled: { x: scaledX, y: scaledY, width: scaledWidth, height: scaledHeight }
        });
        let strokeColor = annotation.id === selectedAnnotationId ? '#1976d2' : '#F44336';

        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = (annotation.id === selectedAnnotationId ? 3 : 2);
        ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);
        if (annotation.id === selectedAnnotationId) {
          const handleSize = 12;
          // four handles at corners
          ctx.fillStyle = '#1976d2';
          ctx.fillRect(scaledX - handleSize / 2, scaledY - handleSize / 2, handleSize, handleSize); // nw
          ctx.fillRect(scaledX + scaledWidth - handleSize / 2, scaledY - handleSize / 2, handleSize, handleSize); // ne
          ctx.fillRect(scaledX - handleSize / 2, scaledY + scaledHeight - handleSize / 2, handleSize, handleSize); // sw
          ctx.fillRect(scaledX + scaledWidth - handleSize / 2, scaledY + scaledHeight - handleSize / 2, handleSize, handleSize); // se
        }
      } else if (annotation.type === 'text') {
        ctx.fillStyle = annotation.id === selectedAnnotationId ? '#1976d2' : 'blue';
        ctx.font = `16px Arial`;
        ctx.fillText(annotation.text, annotation.x * scaleX, annotation.y * scaleY);
      } else if (isLineLikeAnnotation(annotation) && annotation.endX !== undefined && annotation.endY !== undefined) {
        ctx.strokeStyle = annotation.id === selectedAnnotationId ? '#1976d2' : '#F44336';
        ctx.lineWidth = (annotation.id === selectedAnnotationId ? 3 : 2);
        if (annotation.type === 'arrow') {
          drawCanvasArrow(
            ctx,
            annotation.startX * scaleX,
            annotation.startY * scaleY,
            annotation.endX * scaleX,
            annotation.endY * scaleY,
            { color: annotation.id === selectedAnnotationId ? '#1976d2' : '#F44336', lineWidth: annotation.id === selectedAnnotationId ? 3 : 2 }
          );
        } else {
          ctx.beginPath();
          ctx.moveTo(annotation.startX * scaleX, annotation.startY * scaleY);
          ctx.lineTo(annotation.endX * scaleX, annotation.endY * scaleY);
          ctx.stroke();
        }
        if (annotation.id === selectedAnnotationId) {
          const r = 6;
          ctx.fillStyle = '#1976d2';
          ctx.beginPath();
          ctx.arc(annotation.startX * scaleX, annotation.startY * scaleY, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(annotation.endX * scaleX, annotation.endY * scaleY, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });

    // Defect caption on each box, numbered when there is more than one box.
    // Editor-only: reports draw boxes from defect coordinates without captions.
    const labelledBoxes = isSimpleDrawingMode ? [] : annotations.filter(isDefectBoxAnnotation);
    labelledBoxes.forEach((annotation, boxIndex) => {
      const names = getAnnotationDefects(annotation);
      const showNumber = labelledBoxes.length > 1;
      if (names.length === 0 && !showNumber) return;
      const label = `${showNumber ? `${boxIndex + 1}${names.length ? ': ' : ''}` : ''}${names.join(', ')}`;
      const coords = annotationToDefectCoordinates(annotation);
      const isSelected = annotation.id === selectedAnnotationId;
      ctx.font = 'bold 13px Arial';
      const padding = 4;
      const labelHeight = 18;
      const labelWidth = ctx.measureText(label).width + padding * 2;
      const labelX = Math.min(Math.max(coords.x * scaleX, 0), Math.max(canvas.width - labelWidth, 0));
      const boxTop = coords.y * scaleY;
      const labelY = boxTop - labelHeight >= 0 ? boxTop - labelHeight : boxTop;
      ctx.fillStyle = isSelected ? '#1976d2' : '#F44336';
      ctx.fillRect(labelX, labelY, labelWidth, labelHeight);
      ctx.fillStyle = '#FFFFFF';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, labelX + padding, labelY + labelHeight / 2);
      ctx.textBaseline = 'alphabetic';
    });
    
    if (isDrawing && startPoint && currentEndPoint) {
      const drawingColor = '#F44336';
      if (selectedTool === 'line' || selectedTool === 'arrow') {
        if (selectedTool === 'arrow') {
          drawCanvasArrow(ctx, startPoint.x, startPoint.y, currentEndPoint.x, currentEndPoint.y, { color: drawingColor, lineWidth: 2, dashed: true });
        } else {
          ctx.strokeStyle = drawingColor;
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.beginPath();
          ctx.moveTo(startPoint.x, startPoint.y);
          ctx.lineTo(currentEndPoint.x, currentEndPoint.y);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      } else if (selectedTool === 'square') {
        const width = currentEndPoint.x - startPoint.x;
        const height = currentEndPoint.y - startPoint.y;
        ctx.strokeStyle = drawingColor;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(startPoint.x, startPoint.y, width, height);
        ctx.setLineDash([]);
      }
    }
  };

  const hitTestAnnotation = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current) return null;
    const { natX, natY, sx, sy } = getMouseNat(clientX, clientY);
    // Hit test from top-most
    for (let i = annotations.length - 1; i >= 0; i--) {
      const ann = annotations[i];
      if (ann.type === 'square') {
        const ax = ann.x;
        const ay = ann.y;
        const aw = ann.width;
        const ah = ann.height;
        if (natX >= ax && natY >= ay && natX <= ax + aw && natY <= ay + ah) {
          return ann.id;
        }
      } else if (ann.type === 'text') {
        const ax = ann.x;
        const ay = ann.y;
        // simple proximity box in natural coords
        if (Math.abs(natX - ax) <= 10 / sx && Math.abs(natY - ay) <= 10 / sy) {
          return ann.id;
        }
      } else if (isLineLikeAnnotation(ann)) {
        // distance from point to segment threshold
        const px = natX, py = natY;
        const x1 = ann.startX, y1 = ann.startY;
        const x2 = ann.endX, y2 = ann.endY;
        const dx = x2 - x1, dy = y2 - y1;
        const len2 = dx * dx + dy * dy || 1;
        let t = ((px - x1) * dx + (py - y1) * dy) / len2;
        t = Math.max(0, Math.min(1, t));
        const cx = x1 + t * dx, cy = y1 + t * dy;
        const dist = Math.hypot(px - cx, py - cy);
        if (dist <= 8 / Math.max(sx, sy)) {
          return ann.id;
        }
      }
    }
    return null;
  };

  const hitTestHandle = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !selectedAnnotationId) return null;
    const { natX, natY, sx, sy } = getMouseNat(clientX, clientY);
    const ann = annotations.find(a => a.id === selectedAnnotationId);
    if (!ann) return null;
    if (ann.type === 'square') {
      const thrX = 12 / sx;
      const thrY = 12 / sy;
      const corners = {
        nw: { x: ann.x, y: ann.y },
        ne: { x: ann.x + ann.width, y: ann.y },
        sw: { x: ann.x, y: ann.y + ann.height },
        se: { x: ann.x + ann.width, y: ann.y + ann.height },
      };
      for (const [handle, pos] of Object.entries(corners)) {
        if (Math.abs(natX - pos.x) <= thrX && Math.abs(natY - pos.y) <= thrY) {
          return handle;
        }
      }
    } else if (isLineLikeAnnotation(ann)) {
      const thr = 12 / Math.max(sx, sy);
      const dStart = Math.hypot(natX - ann.startX, natY - ann.startY);
      if (dStart <= thr) return 'start';
      const dEnd = Math.hypot(natX - ann.endX, natY - ann.endY);
      if (dEnd <= thr) return 'end';
    }
    return null;
  };

  const imgRectSafe = (img) => {
    const imgRect = img.getBoundingClientRect();
    const sx = imgRect.width / img.naturalWidth;
    const sy = imgRect.height / img.naturalHeight;
    return { imgRect, sx, sy };
  };

  const getMouseNat = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return { natX: 0, natY: 0, sx: 1, sy: 1 };
    const rect = canvas.getBoundingClientRect();
    const xCanvas = (clientX - rect.left - offset.x) / Math.max(scale, 0.0001);
    const yCanvas = (clientY - rect.top - offset.y) / Math.max(scale, 0.0001);
    const { sx, sy } = imgRectSafe(img);
    const natX = xCanvas / (sx || 1);
    const natY = yCanvas / (sy || 1);
    return { natX, natY, sx, sy };
  };

  // Keep the label readable while fitting inside the marker box.
  const getPlanFontPx = React.useCallback((containerRef, ann, label = '') => {
    const basePx = sitePlanAnnotationFontSize || 18;
    const containerWidth = containerRef?.current?.clientWidth || 360;
    const containerHeight = containerRef?.current?.clientHeight || 240;
    const annPixelWidth = Math.max((ann?.width || 0) * containerWidth, 1);
    const annPixelHeight = Math.max((ann?.height || 0) * containerHeight, 1);
    const maxDigits = 4;
    const labelLength = Math.min(Math.max(String(label || '').trim().length, 1), maxDigits);
    const widthBound = annPixelWidth / (labelLength * 0.78);
    const heightBound = annPixelHeight * 0.62;
    const scaled = Math.min(widthBound, heightBound, basePx);
    return Math.max(5, Math.round(scaled));
  }, [sitePlanAnnotationFontSize]);

  const findNonOverlappingSitePlanPosition = React.useCallback((x, y, width, height, anns, ignoreId = null) => {
    const gap = 0.008;
    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
    const rectsOverlap = (a, b) => !(a.right + gap <= b.left || b.right + gap <= a.left || a.bottom + gap <= b.top || b.bottom + gap <= a.top);
    const existingRects = (anns || [])
      .filter((ann) => String(ann?.id ?? '') !== String(ignoreId ?? ''))
      .map((ann) => ({
        left: Number(ann.x) || 0,
        top: Number(ann.y) || 0,
        right: (Number(ann.x) || 0) + Math.max(Number(ann.width) || width, 0.001),
        bottom: (Number(ann.y) || 0) + Math.max(Number(ann.height) || height, 0.001),
      }));
    const candidates = [
      { x, y },
      { x: x + width + gap, y },
      { x, y: y + height + gap },
      { x: x - width - gap, y },
      { x, y: y - height - gap },
      { x: x + width + gap, y: y + height + gap },
      { x: x - width - gap, y: y + height + gap },
      { x: x + width + gap, y: y - height - gap },
      { x: x - width - gap, y: y - height - gap },
    ];
    let best = { x: clamp(x, 0, 1 - width), y: clamp(y, 0, 1 - height) };
    let bestScore = null;
    candidates.forEach((candidate) => {
      const placed = { x: clamp(candidate.x, 0, 1 - width), y: clamp(candidate.y, 0, 1 - height) };
      const rect = { left: placed.x, top: placed.y, right: placed.x + width, bottom: placed.y + height };
      const overlapCount = existingRects.reduce((count, used) => count + (rectsOverlap(rect, used) ? 1 : 0), 0);
      const shiftScore = Math.abs(placed.x - x) + Math.abs(placed.y - y);
      const score = overlapCount * 1000 + shiftScore;
      if (bestScore === null || score < bestScore) {
        bestScore = score;
        best = placed;
      }
    });
    return best;
  }, []);

  const normalizeSquare = (ann) => {
    if (!ann || ann.type !== 'square') return ann;
    let { x, y, width, height } = ann;
    if (width < 0) {
      x = x + width;
      width = Math.abs(width);
    }
    if (height < 0) {
      y = y + height;
      height = Math.abs(height);
    }
    return { ...ann, x, y, width, height };
  };

  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;
    
    // Convert to canvas coordinates considering scale and offset
    const x = (clientX - offset.x) / scale;
    const y = (clientY - offset.y) / scale;
    
    if (e.ctrlKey || e.button === 1) {
      // Start panning
      setIsPanning(true);
      setStartPoint({ x: clientX, y: clientY });
      return;
    }
    
    // Selection/resize/move takes precedence if clicking on an existing annotation
    {
      // Test handles first if something is selected
      if (selectedAnnotationId) {
        const handle = hitTestHandle(e.clientX, e.clientY);
        if (handle) {
          const { natX: startNatX, natY: startNatY, sx, sy } = getMouseNat(e.clientX, e.clientY);
          const ann = annotations.find(a => a.id === selectedAnnotationId);
          setAnnDrag({ mode: handle, startNatX, startNatY, annStart: { ...ann }, sx, sy });
          return;
        }
      }
      const id = hitTestAnnotation(e.clientX, e.clientY);
      if (id) {
        setSelectedAnnotationId(id);
        // Normalize selected square if needed to avoid negative sizes
        const annIndex = annotations.findIndex(a => a.id === id);
        if (annIndex >= 0 && annotations[annIndex]?.type === 'square') {
          const norm = normalizeSquare(annotations[annIndex]);
          if (norm !== annotations[annIndex]) {
            const newList = [...annotations];
            newList[annIndex] = norm;
            setAnnotations(newList);
            setTimeout(() => redrawAnnotations(), 0);
          }
        }
        // Begin move if clicked inside any annotation (even when draw tools are active)
        const { natX: startNatX, natY: startNatY, sx, sy } = getMouseNat(e.clientX, e.clientY);
        const baseAnn = annotations.find(a => a.id === id);
        const ann = baseAnn && baseAnn.type === 'square' ? normalizeSquare(baseAnn) : baseAnn;
        // Record grab offset relative to appropriate anchor
        const grabDx = ann.type === 'square' ? (startNatX - ann.x) : (ann.type === 'text' ? (startNatX - ann.x) : 0);
        const grabDy = ann.type === 'square' ? (startNatY - ann.y) : (ann.type === 'text' ? (startNatY - ann.y) : 0);
        setAnnDrag({ mode: 'move', startNatX, startNatY, grabDx, grabDy, annStart: { ...ann }, sx, sy });
        return;
      } else if (!selectedTool) {
        setSelectedAnnotationId(null);
        setIsEditingAnnotation(false);
        setAnnDrag(null);
        // fall through only if no tool and empty click (do nothing)
        return;
      }
    }
    
    if (selectedTool === 'text') {
      const text = (labelText || '').trim();
      if (text) {
        const img = imageRef.current;
        const imgRect = img ? img.getBoundingClientRect() : null;
        const naturalW = img ? img.naturalWidth : null;
        const naturalH = img ? img.naturalHeight : null;
        const sx = imgRect && naturalW ? imgRect.width / naturalW : 1;
        const sy = imgRect && naturalH ? imgRect.height / naturalH : 1;
        const newAnnotation = {
          type: 'text',
          x: x / sx,
          y: y / sy,
          text,
          id: Date.now()
        };
        setAnnotations(prev => [...prev, newAnnotation]);
        // Trigger immediate redraw to show text annotation
        setTimeout(() => redrawAnnotations(), 0);
      }
    } else if (selectedTool) {
      console.log('Mouse down - starting drawing:', { x, y, selectedTool });
      console.log('Setting drawing state: isDrawing=true, startPoint=', { x, y });
      setIsDrawing(true);
      setStartPoint({ x, y });
      setCurrentEndPoint({ x, y });
    }
  };
const handleMouseMove = (e) => {
  if (!canvasRef.current) return;

  const canvas = canvasRef.current;
  const rect = canvas.getBoundingClientRect();
  const clientX = e.clientX - rect.left;
  const clientY = e.clientY - rect.top;

  // --- Handle panning ---
  if (isPanning && startPoint) {
    const deltaX = clientX - startPoint.x;
    const deltaY = clientY - startPoint.y;
    setOffset((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));
    setStartPoint({ x: clientX, y: clientY });
    return;
  }

  // --- Handle drawing ---
  if (isDrawing && startPoint) {
    // convert both startPoint & current mouse to canvas space
    const startX = (startPoint.x - offset.x) / scale;
    const startY = (startPoint.y - offset.y) / scale;
    const x = (clientX - offset.x) / scale;
    const y = (clientY - offset.y) / scale;

    const ctx = canvas.getContext("2d");
    redrawAnnotations(); // clear + draw previous annotations

    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);

    const drawingColor = '#F44336';

    ctx.strokeStyle = drawingColor;
    ctx.lineWidth = 2 / scale;
    ctx.setLineDash([5, 5]);

    if (selectedTool === "line" || selectedTool === "arrow") {
      if (selectedTool === "arrow") {
        drawCanvasArrow(ctx, startX, startY, x, y, { color: drawingColor, lineWidth: 2 / scale, dashed: true });
      } else {
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
    } else if (selectedTool === "square") {
      ctx.strokeRect(startX, startY, x - startX, y - startY);
    }

    ctx.setLineDash([]);
    ctx.restore();

    setCurrentEndPoint({ x, y });
  }
  // --- Handle selection resize/move ---
  if (annDrag && selectedAnnotationId) {
    // Only drag when primary button is held
    if ((e.buttons & 1) === 0) {
      return;
    }
    const { natX, natY, sx, sy } = getMouseNat(e.clientX, e.clientY);
    const annStart = annDrag.annStart;
    let updatedAnn = { ...annStart };
    const minSize = 5 / Math.max(sx, sy);
    if (annDrag.mode === 'move') {
      if (annStart.type === 'square') {
        // Maintain initial grab offset so the shape doesn't jump
        updatedAnn.x = natX - (annDrag.grabDx ?? 0);
        updatedAnn.y = natY - (annDrag.grabDy ?? 0);
        // Clamp within image bounds
        const imgEl = imageRef.current;
        const natW = imgEl?.naturalWidth || 0;
        const natH = imgEl?.naturalHeight || 0;
        const maxX = Math.max(0, natW - (annStart.width || 0));
        const maxY = Math.max(0, natH - (annStart.height || 0));
        updatedAnn.x = Math.min(Math.max(updatedAnn.x, 0), maxX || updatedAnn.x);
        updatedAnn.y = Math.min(Math.max(updatedAnn.y, 0), maxY || updatedAnn.y);
      } else if (annStart.type === 'text') {
        updatedAnn.x = natX - (annDrag.grabDx ?? 0);
        updatedAnn.y = natY - (annDrag.grabDy ?? 0);
      } else if (isLineLikeAnnotation(annStart)) {
        const dx = natX - annDrag.startNatX;
        const dy = natY - annDrag.startNatY;
        updatedAnn.startX = annStart.startX + dx;
        updatedAnn.startY = annStart.startY + dy;
        updatedAnn.endX = annStart.endX + dx;
        updatedAnn.endY = annStart.endY + dy;
      }
    } else if (annDrag.mode === 'nw') {
      const newX = natX;
      const newY = natY;
      const newW = (annStart.x + annStart.width) - newX;
      const newH = (annStart.y + annStart.height) - newY;
      updatedAnn.x = newX;
      updatedAnn.y = newY;
      updatedAnn.width = Math.max(newW, minSize);
      updatedAnn.height = Math.max(newH, minSize);
    } else if (annDrag.mode === 'ne') {
      const newY = natY;
      const newW = natX - annStart.x;
      const newH = (annStart.y + annStart.height) - newY;
      updatedAnn.y = newY;
      updatedAnn.width = Math.max(newW, minSize);
      updatedAnn.height = Math.max(newH, minSize);
    } else if (annDrag.mode === 'sw') {
      const newX = natX;
      const newW = (annStart.x + annStart.width) - newX;
      const newH = natY - annStart.y;
      updatedAnn.x = newX;
      updatedAnn.width = Math.max(newW, minSize);
      updatedAnn.height = Math.max(newH, minSize);
    } else if (annDrag.mode === 'se') {
      const newW = natX - annStart.x;
      const newH = natY - annStart.y;
      updatedAnn.width = Math.max(newW, minSize);
      updatedAnn.height = Math.max(newH, minSize);
    } else if (annDrag.mode === 'start' && isLineLikeAnnotation(annStart)) {
      updatedAnn.startX = natX;
      updatedAnn.startY = natY;
    } else if (annDrag.mode === 'end' && isLineLikeAnnotation(annStart)) {
      updatedAnn.endX = natX;
      updatedAnn.endY = natY;
    }
    const newList = annotations.map(a => a.id === selectedAnnotationId ? updatedAnn : a);
    setAnnotations(newList);
    setTimeout(() => redrawAnnotations(), 0);
    return;
  }
};

  const handleMouseMove1 = (e) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;
    
    if (isPanning && startPoint) {
      const deltaX = clientX - startPoint.x;
      const deltaY = clientY - startPoint.y;
      setOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      setStartPoint({ x: clientX, y: clientY });
      return;
    }
    
    if (isDrawing && startPoint) {
      const x = (clientX - offset.x) / scale;
      const y = (clientY - offset.y) / scale;
      console.log('Mouse move - updating currentEndPoint:', { x, y, isDrawing, startPoint });
      console.log('About to set currentEndPoint to:', { x, y });
      setCurrentEndPoint({ x, y });
    }
  };

  const handleMouseUp = (e) => {
    if (isPanning) {
      setIsPanning(false);
      setStartPoint(null);
      return;
    }
    
    if (!isDrawing || !startPoint || !currentEndPoint) {
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentEndPoint(null);
      return;
    }
    
    let newAnnotation;
    
    if (selectedTool === 'line' || selectedTool === 'arrow') {
      const img = imageRef.current;
      const imgRect = img ? img.getBoundingClientRect() : null;
      const naturalW = img ? img.naturalWidth : null;
      const naturalH = img ? img.naturalHeight : null;
      const sx = imgRect && naturalW ? imgRect.width / naturalW : 1;
      const sy = imgRect && naturalH ? imgRect.height / naturalH : 1;
      newAnnotation = {
        type: selectedTool === 'arrow' ? 'arrow' : 'line',
        startX: startPoint.x / sx,
        startY: startPoint.y / sy,
        endX: currentEndPoint.x / sx,
        endY: currentEndPoint.y / sy,
        severityCategory: annotationSeverity,
        context: imageType,
        id: Date.now()
      };
    } else if (selectedTool === 'square') {
      const img = imageRef.current;
      const imgRect = img ? img.getBoundingClientRect() : null;
      const naturalW = img ? img.naturalWidth : null;
      const naturalH = img ? img.naturalHeight : null;
      const sx = imgRect && naturalW ? imgRect.width / naturalW : 1;
      const sy = imgRect && naturalH ? imgRect.height / naturalH : 1;
      // Normalize to ensure width/height are positive and (x,y) is top-left
      let nx = startPoint.x / sx;
      let ny = startPoint.y / sy;
      let nw = (currentEndPoint.x - startPoint.x) / sx;
      let nh = (currentEndPoint.y - startPoint.y) / sy;
      if (nw < 0) {
        nx = nx + nw;
        nw = Math.abs(nw);
      }
      if (nh < 0) {
        ny = ny + nh;
        nh = Math.abs(nh);
      }
      newAnnotation = {
        type: 'square',
        x: nx,
        y: ny,
        width: nw,
        height: nh,
        text: '',
        severityCategory: annotationSeverity,
        context: imageType,
        id: Date.now()
      };
    }
    
    if (newAnnotation) {
      const updatedAnnotations = [...annotations, newAnnotation];
      setAnnotations(updatedAnnotations);
      // Select the new box so the next defects chosen are attached to it
      setSelectedAnnotationId(newAnnotation.id);
      
      // Save annotations to localStorage
      if (image) {
        const imageKey = getImageKey(image);
        if (imageKey) {
          try {
            localStorage.setItem(imageKey, JSON.stringify(updatedAnnotations));
            console.log('Annotations saved to localStorage for key:', imageKey);
          } catch (error) {
            console.error('Error saving annotations to localStorage:', error);
          }
        }
      }
      
      // Trigger redraw to show the final annotation
      setTimeout(() => redrawAnnotations(), 0);
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentEndPoint(null);
    if (annDrag && selectedAnnotationId) {
      // Persist selection edits
      if (image) {
        const imageKey = getImageKey(image);
        if (imageKey) {
          try {
            localStorage.setItem(imageKey, JSON.stringify(annotations));
          } catch {}
        }
      }
      setAnnDrag(null);
      setTimeout(() => redrawAnnotations(), 0);
    }
  };

  const deleteSelectedAnnotation = async () => {
    if (!selectedAnnotationId) return;
    setAnnDrag(null);
    const updated = annotations.filter(a => a.id !== selectedAnnotationId);
    setAnnotations(updated);
    setSelectedAnnotationId(null);
    // Save to localStorage if possible
    if (image) {
      const imageKey = getImageKey(image);
      if (imageKey) {
        try {
          localStorage.setItem(imageKey, JSON.stringify(updated));
        } catch {}
      }
    }
    if (isSimpleDrawingMode && image) {
      await persistSimpleDrawingDraft(updated);
    }
    setTimeout(() => redrawAnnotations(), 0);
  };

  const beginEditSelectedAnnotation = () => {
    if (!selectedAnnotationId) return;
    setAnnDrag(null);
    const ann = annotations.find(a => a.id === selectedAnnotationId);
    if (!ann) return;
    setEditingText(ann.text || '');
    setIsEditingAnnotation(true);
  };

  const applyEditSelectedAnnotation = () => {
    if (!selectedAnnotationId) return;
    setAnnDrag(null);
    const updated = annotations.map(a => a.id === selectedAnnotationId ? { ...a, text: editingText } : a);
    setAnnotations(updated);
    setIsEditingAnnotation(false);
    // persist
    if (image) {
      const imageKey = getImageKey(image);
      if (imageKey) {
        try {
          localStorage.setItem(imageKey, JSON.stringify(updated));
        } catch {}
      }
    }
    setTimeout(() => redrawAnnotations(), 0);
  };

  const rotateSelectedAnnotation = () => {
    if (!selectedAnnotationId) return;
    setAnnDrag(null);
    const ann = annotations.find(a => a.id === selectedAnnotationId);
    if (!ann) return;
    let rotated = ann;
    if (ann.type === 'square') {
      // rotate 90deg: swap width/height around center
      const cx = ann.x + (ann.width || 0) / 2;
      const cy = ann.y + (ann.height || 0) / 2;
      const newW = ann.height || 0;
      const newH = ann.width || 0;
      const newX = cx - newW / 2;
      const newY = cy - newH / 2;
      rotated = { ...ann, x: newX, y: newY, width: newW, height: newH };
    } else if (isLineLikeAnnotation(ann) && ann.endX !== undefined && ann.endY !== undefined) {
      // rotate 90deg around midpoint
      const mx = (ann.startX + ann.endX) / 2;
      const my = (ann.startY + ann.endY) / 2;
      const rotatePoint = (px, py) => {
        const dx = px - mx;
        const dy = py - my;
        // 90deg CW: (x,y)->(y,-x)
        const rx = my + (-dy);
        const ry = mx + dx;
        // The above mapping is not correct in normalized coords; use rotation matrix:
        // [0 1; -1 0] * [dx; dy] + [mx; my]
        const rdx = 0 * dx + 1 * dy;
        const rdy = -1 * dx + 0 * dy;
        return { x: mx + rdx, y: my + rdy };
      };
      const p1 = rotatePoint(ann.startX, ann.startY);
      const p2 = rotatePoint(ann.endX, ann.endY);
      rotated = { ...ann, startX: p1.x, startY: p1.y, endX: p2.x, endY: p2.y };
    } else if (ann.type === 'text') {
      // no-op for text; could implement orientation later
      rotated = ann;
    }
    const updated = annotations.map(a => a.id === selectedAnnotationId ? rotated : a);
    setAnnotations(updated);
    if (image) {
      const imageKey = getImageKey(image);
      if (imageKey) {
        try {
          localStorage.setItem(imageKey, JSON.stringify(updated));
        } catch {}
      }
    }
    setTimeout(() => redrawAnnotations(), 0);
  };

  const updateAnnotationDefects = (annotationId, updater) => {
    const updated = annotations.map((a) => (
      a.id === annotationId ? { ...a, defects: updater(getAnnotationDefects(a)) } : a
    ));
    setAnnotations(updated);
  };

  const addDefect = () => {
    const type = String(newDefect || '').trim();
    if (!type) return;
    if (targetDefectBox) {
      updateAnnotationDefects(targetDefectBox.id, (current) => (
        current.some((d) => d.toLowerCase() === type.toLowerCase()) ? current : [...current, type]
      ));
      setNewDefect('');
      return;
    }
    setDefects((prev) => {
      const prevList = Array.isArray(prev) ? prev : [];
      const idx = prevList.findIndex((d) => d && String(d.type || '').trim().toLowerCase() === type.toLowerCase());
      let next;
      if (idx >= 0) {
        next = prevList.map((d, i) =>
          i === idx ? { ...d, count: (Number(d.count) || 1) + 1 } : d
        );
      } else {
        next = [...prevList, { type, count: 1 }];
      }
      persistAnnotationDataToLocalStorage(next, recommendation);
      return next;
    });
    setNewDefect('');
  };

  const removeDefect = (index) => {
    setDefects((prev) => {
      const prevList = Array.isArray(prev) ? prev : [];
      const next = prevList.filter((_, i) => i !== index);
      persistAnnotationDataToLocalStorage(next, recommendation);
      return next;
    });
  };

  const removeBoxDefect = (annotationId, defectIndex) => {
    updateAnnotationDefects(annotationId, (current) => current.filter((_, i) => i !== defectIndex));
  };

  // YOLO detection function
  const handleYOLODetection = async () => {
    if (!isYoloEligibleImage) {
      console.log('Skipping YOLO detection for non-inspection image:', image?.id);
      return;
    }
    
    setIsDetecting(true);
    try {
      console.log('Starting YOLO detection for image:', image.id);
      
      // Call the YOLO detection API
      const detectionResponse = await apiService.detectObjects(
        image.id, 
        confidenceThreshold, 
        false, // Don't auto-save, we'll handle annotations manually
        blurCars // Pass blur cars option
      );
      
      if (detectionResponse && detectionResponse.detections) {
        console.log('YOLO detection results:', detectionResponse.detections);
        setDetectionResults(detectionResponse.detections);
        
        // Handle blurred image if available
        if (detectionResponse.blurred_image_url) {
          console.log('Blurred image available:', detectionResponse.blurred_image_url);
          setBlurredImageUrl(`${apiService.baseURL}${detectionResponse.blurred_image_url}`);
        } else {
          setBlurredImageUrl(null);
        }
        
        // Convert YOLO detections to annotations
        const newAnnotations = detectionResponse.detections.map((detection, index) => ({
          type: 'square',
          x: detection.x,
          y: detection.y,
          width: detection.width,
          height: detection.height,
          text: `${detection.defect_type || detection.class_name} (${(detection.confidence * 100).toFixed(1)}%)`,
          id: `yolo_${Date.now()}_${index}`,
          confidence: detection.confidence,
          class: detection.class_name,
          context: imageType
        }));
        
        // Add new annotations to existing ones
        setAnnotations(prev => [...prev, ...newAnnotations]);
        
        // Auto-add detected defect types (from defect_type table)
        const detectedDefects = [...new Set(
          (detectionResponse.detections || [])
            .filter((d) => d.detection_type === 'defect' && d.defect_type)
            .map((d) => d.defect_type)
        )];
        setDefects((prev) => {
          const prevList = Array.isArray(prev) ? prev : [];
          let next = [...prevList];
          for (const type of detectedDefects) {
            const normalizedType = String(type || '').trim();
            if (!normalizedType) continue;
            const idx = next.findIndex((d) => d && d.type === normalizedType);
            if (idx === -1) next = [...next, { type: normalizedType, count: 1 }];
          }
          persistAnnotationDataToLocalStorage(next, recommendation);
          return next;
        });
        
        console.log(`YOLO detected ${newAnnotations.length} objects`);
        
        // Trigger canvas redraw to show new annotations
        setTimeout(() => redrawAnnotations(), 100);
      } else {
        console.log('No objects detected by YOLO');
      }
    } catch (error) {
      console.error('Error during YOLO detection:', error);
      // You might want to show a user-friendly error message here
    } finally {
      setIsDetecting(false);
    }
  };

  // Auto-detect and blur sensitive objects (people/vehicles) on open
  useEffect(() => {
    let cancelled = false;
    const runAutoDetect = async () => {
      if (!isYoloEligibleImage) return;
      try {
        setIsDetecting(true);
        const res = await apiService.detectObjects(
          image.id,
          confidenceThreshold,
          false,     // auto_save
          true,      // blurCars (also used by backend for people/vehicles)
          true       // auto_detect flag
        );
        if (cancelled) return;
        const dets = res?.detections || [];
        setDetectionResults(dets);
        const classes = new Set(
          dets.map(d => String(d.class_name || '').toLowerCase())
        );
        const hasSensitive =
          classes.has('person') ||
          classes.has('car') || classes.has('truck') ||
          classes.has('bus') || classes.has('motorcycle') ||
          classes.has('bicycle') || classes.has('vehicle') || classes.has('van');
        if (hasSensitive && res?.blurred_image_url) {
          setBlurredImageUrl(`${apiService.baseURL}${res.blurred_image_url}`);
        } else {
          setBlurredImageUrl(null);
        }
      } catch (e) {
        console.error('Auto-detect/blur failed:', e);
      } finally {
        if (!cancelled) setIsDetecting(false);
      }
    };
    runAutoDetect();
    return () => { cancelled = true; };
  }, [image?.id, confidenceThreshold, isYoloEligibleImage]);

  // Keep level in sync when image changes
  useEffect(() => {
    setLevelInfo(image?.floor_name || '');
  }, [image?.floor_name, image?.id]);

  useEffect(() => {
    try {
      localStorage.setItem('autoAdvanceOnSave', JSON.stringify(autoAdvanceOnSave));
    } catch {
    }
  }, [autoAdvanceOnSave]);

  useEffect(() => {
    if (!open) return;
    if (isCoverElevationAnnotationImage) {
      setSelectedTool('square');
      setLabelText('');
    }
  }, [open, isCoverElevationAnnotationImage, image?.id]);
  

  const persistSitePlanAnnotations = async (byPlanOverride) => {
    if (!buildingId) {
        console.error('Cannot save site plan annotations: No buildingId provided');
        return;
    }
    try {
      // Fetch current annotations to preserve other fields like elevation_map
      const current = await apiService.getSitePlanAnnotations(buildingId).catch(() => ({}));
      
      const payload = {
        ...current,
        by_plan_url: {},
      };
      const existingByPlan =
        (current && current.by_plan_url && typeof current.by_plan_url === 'object') ? current.by_plan_url : {};
      Object.entries(existingByPlan).forEach(([url, anns]) => {
        if (!Array.isArray(anns)) return;
        const key = normalizePlanUrlKey(url) || url;
        payload.by_plan_url[key] = [
          ...(Array.isArray(payload.by_plan_url[key]) ? payload.by_plan_url[key] : []),
          ...anns,
        ];
      });
      const source = byPlanOverride || sitePlanAnnotationsByPlan || {};
      const normalizedSource = {};
      Object.entries(source).forEach(([url, anns]) => {
        if (!url || !Array.isArray(anns)) return;
        const planKey = normalizePlanUrlKey(url) || url;
        normalizedSource[planKey] = anns;
      });
      const movedSource = Object.entries(normalizedSource).reduce((acc, [planKey, anns]) => {
        const next = movePlanLinkedAnnotationsToTarget(
          {
            ...acc,
            [planKey]: anns,
          },
          planKey,
          anns
        );
        return next;
      }, {});
      Object.entries(movedSource).forEach(([url, anns]) => {
        if (!url || !Array.isArray(anns)) return;
        const planKey = normalizePlanUrlKey(url) || url;
        // Deduplicate by (image_id|id|label, context)
        const dedup = new Map();
        (Array.isArray(payload.by_plan_url[planKey]) ? payload.by_plan_url[planKey] : []).forEach((ann) => {
          const keyId = ann.image_id != null
            ? String(ann.image_id)
            : (ann.id != null ? String(ann.id) : String(ann.label || ''));
          const key = `${keyId}|${ann.context || 'drone'}`;
          dedup.set(key, ann);
        });
        anns.forEach((ann) => {
          const keyId = ann.image_id != null
            ? String(ann.image_id)
            : (ann.id != null ? String(ann.id) : String(ann.label || ''));
          const key = `${keyId}|${ann.context || 'drone'}`;
          dedup.set(key, ann);
        });
        const unique = Array.from(dedup.values());
        payload.by_plan_url[planKey] = unique.map((ann) => ({
          id: ann.image_id != null
            ? String(ann.image_id)
            : (ann.id != null ? String(ann.id) : String(ann.label || '')),
          image_id: ann.image_id === null
            ? null
            : (ann.image_id != null ? String(ann.image_id) : (ann.id != null ? String(ann.id) : null)),
          label: resolveSitePlanLabel(ann),
          x: typeof ann.x === 'number' ? ann.x : 0,
          y: typeof ann.y === 'number' ? ann.y : 0,
          width: typeof ann.width === 'number' ? ann.width : 0,
          height: typeof ann.height === 'number' ? ann.height : 0,
          startX: typeof ann.startX === 'number' ? ann.startX : (typeof ann.start_x === 'number' ? ann.start_x : undefined),
          startY: typeof ann.startY === 'number' ? ann.startY : (typeof ann.start_y === 'number' ? ann.start_y : undefined),
          endX: typeof ann.endX === 'number' ? ann.endX : (typeof ann.end_x === 'number' ? ann.end_x : undefined),
          endY: typeof ann.endY === 'number' ? ann.endY : (typeof ann.end_y === 'number' ? ann.end_y : undefined),
          severity: ann.severity || ann.severityCategory || 'repair',
          context: ann.context || 'drone',
          level: ann.level || levelInfo || '',
          type: ann.type || 'square',
          coordinate_mode: ann.coordinate_mode || ann.coordinateMode || 'normalized',
          render_style: ann.render_style || ann.renderStyle || '',
          suppress_label: Boolean(ann.suppress_label || ann.suppressLabel)
        }));
      });
      const movedKeys = new Set();
      Object.values(movedSource).forEach((anns) => {
        (Array.isArray(anns) ? anns : []).forEach((ann) => {
          const linkKey = annotationPlanLinkKey(ann);
          if (linkKey) movedKeys.add(linkKey);
        });
      });
      if (movedKeys.size > 0) {
        Object.entries(payload.by_plan_url).forEach(([url, anns]) => {
          const normalizedUrl = normalizePlanUrlKey(url) || url;
          if (Object.prototype.hasOwnProperty.call(movedSource, normalizedUrl) || !Array.isArray(anns)) {
            return;
          }
          payload.by_plan_url[normalizedUrl] = anns.filter((ann) => !movedKeys.has(annotationPlanLinkKey(ann)));
        });
      }
      console.log('Saving site plan annotations payload:', payload);
      await apiService.saveSitePlanAnnotations(buildingId, payload);
    } catch (e) {
      console.error('Error saving site plan annotations:', e);
      throw e;
    }
  };

  const persistCoverElevationAnnotations = async (targetImageId, annotationsOverride) => {
    if (!buildingId || !targetImageId) {
      console.error('Cannot save cover elevation annotations: missing buildingId or imageId');
      return;
    }
    try {
      const current = await apiService.getSitePlanAnnotations(buildingId).catch(() => ({}));
      const payload = {
        ...current,
        cover_annotations_by_image_id: {
          ...((current && current.cover_annotations_by_image_id && typeof current.cover_annotations_by_image_id === 'object')
            ? current.cover_annotations_by_image_id
            : {}),
        },
      };
      const source = Array.isArray(annotationsOverride) ? annotationsOverride : annotations;
      const existingForImage = Array.isArray(payload.cover_annotations_by_image_id[String(targetImageId)])
        ? payload.cover_annotations_by_image_id[String(targetImageId)]
        : [];
      const preservedOtherContexts = existingForImage.filter(
        (ann) => String(ann?.context || 'drone').trim().toLowerCase() !== String(imageType || 'drone').trim().toLowerCase()
      );
      const normalized = source
        .filter((ann) => ann && ann.type === 'square')
        .map((ann, index) => ({
          id: String(ann.id || `${targetImageId}_${index + 1}`),
          image_id: null,
          label: '',
          x: typeof ann.x === 'number' ? ann.x : 0,
          y: typeof ann.y === 'number' ? ann.y : 0,
          width: typeof ann.width === 'number' ? ann.width : 0,
          height: typeof ann.height === 'number' ? ann.height : 0,
          severity: 'repair',
          context: imageType || 'drone',
          type: 'square',
          coordinate_mode: 'pixel',
          render_style: 'outline',
          suppress_label: true,
        }));
      payload.cover_annotations_by_image_id[String(targetImageId)] = [...preservedOtherContexts, ...normalized];
      await apiService.saveSitePlanAnnotations(buildingId, payload);
      setCoverAnnotationsByImageId((prev) => ({
        ...(prev || {}),
        [String(targetImageId)]: [...preservedOtherContexts, ...normalized],
      }));
    } catch (e) {
      console.error('Error saving cover elevation annotations:', e);
      throw e;
    }
  };

  const persistSimpleDrawingDraft = async (annotationsOverride) => {
    const nextAnnotations = Array.isArray(annotationsOverride) ? annotationsOverride : annotations;
    const imageKey = getImageKey(image);
    if (imageKey) {
      try {
        localStorage.setItem(imageKey, JSON.stringify(nextAnnotations || []));
      } catch (e) {
        console.error('Error saving drawing draft to localStorage:', e);
      }
    }

    if (!buildingId || !image) return;

    if (isCoverElevationAnnotationImage && image.id) {
      try {
        await persistCoverElevationAnnotations(image.id, nextAnnotations);
      } catch (e) {
        console.error('Error saving cover elevation draft to backend:', e);
      }
      return;
    }

    const url = image.file_url || image.url || image.src;
    if (!url) return;

      const mappedAnnotations = (nextAnnotations || []).map((ann) => ({
      ...ann,
      label: ann.text || ann.label || '',
      image_id: null,
      x: typeof ann.x === 'number' ? ann.x : (typeof ann.startX === 'number' ? ann.startX : 0),
      y: typeof ann.y === 'number' ? ann.y : (typeof ann.startY === 'number' ? ann.startY : 0),
      width: typeof ann.width === 'number' ? ann.width : (typeof ann.startX === 'number' && typeof ann.endX === 'number' ? ann.endX - ann.startX : 0),
      height: typeof ann.height === 'number' ? ann.height : (typeof ann.startY === 'number' && typeof ann.endY === 'number' ? ann.endY - ann.startY : 0),
      severity: ann.severity || ann.severityCategory || 'repair',
      context: ann.context || imageType || 'drone',
      coordinate_mode: 'pixel',
      render_style: 'outline',
      suppress_label: !String(ann.text || ann.label || '').trim()
    }));

    try {
      await persistSitePlanAnnotations({
        [url]: mappedAnnotations
      });
    } catch (e) {
      console.error('Error saving drawing draft to backend:', e);
    }
  };

  const annotationToDefectCoordinates = (annotation) => {
    if (!annotation) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    if (isLineLikeAnnotation(annotation)) {
      const startX = Number(annotation.startX) || 0;
      const startY = Number(annotation.startY) || 0;
      const endX = Number(annotation.endX) || startX;
      const endY = Number(annotation.endY) || startY;
      return {
        x: Math.min(startX, endX),
        y: Math.min(startY, endY),
        width: Math.abs(endX - startX),
        height: Math.abs(endY - startY),
      };
    }
    return {
      x: Number(annotation.x) || 0,
      y: Number(annotation.y) || 0,
      width: Number(annotation.width) || 0,
      height: Number(annotation.height) || 0,
    };
  };

  const buildDefectSaveEntries = () => {
    // One row per defect per box, using that box's coordinates
    const boxEntries = [];
    defectBoxes.forEach((box) => {
      getAnnotationDefects(box).forEach((type) => {
        boxEntries.push({ type, coordinates: annotationToDefectCoordinates(box) });
      });
    });

    const expandedDefectTypes = expandDefectTypes(normalizeDefects(defects));
    if (boxEntries.length > 0) {
      // Image-level defects are saved without a box so they reload as image-level
      return [
        ...boxEntries,
        ...expandedDefectTypes.map((type) => ({ type, coordinates: annotationToDefectCoordinates(null) })),
      ];
    }
    if (expandedDefectTypes.length > 0) {
      return expandedDefectTypes.map((type, index) => ({
        type,
        coordinates: annotationToDefectCoordinates(annotations[index] || annotations[0]),
      }));
    }

    if (annotations.length > 0) {
      return annotations.map((annotation) => ({
        type: String(annotation.text || annotation.label || '').trim() || 'General Defect',
        coordinates: annotationToDefectCoordinates(annotation),
      }));
    }

    if (String(recommendation || '').trim()) {
      return [{ type: 'General Defect', coordinates: annotationToDefectCoordinates(null) }];
    }

    return [];
  };

  const syncImageDefectsToDatabase = async () => {
    if (!image?.id) return;

    try {
      const existing = await apiService.getImageDefects(image.id);
      if (existing && Array.isArray(existing.defects) && existing.defects.length > 0) {
        for (const d of existing.defects) {
          if (d && d.id) {
            try {
              await apiService.deleteDefectAnnotation(image.id, d.id);
            } catch (e) {}
          }
        }
      }
    } catch (e) {}

    const entries = buildDefectSaveEntries();
    const recommendationText = normalizeRecommendationValue(recommendation) || 'No specific recommendation';

    for (const entry of entries) {
      const annotationData = {
        image_id: image.id,
        coordinates: [entry.coordinates],
        defect_type: entry.type || 'General Defect',
        recommendation: recommendationText,
        project_id: image?.project_id || null,
      };
      try {
        await apiService.addDefectAnnotation(annotationData);
      } catch (error) {
        console.error('Error saving defect annotation:', error);
      }
    }
  };

  const handleDismiss = async () => {
    if (isSimpleDrawingMode && image) {
      await persistSimpleDrawingDraft();
    }
    onClose?.();
  };

  const resetForImageNavigation = () => {
    if (imageRef.current) {
      imageRef.current.removeAttribute('src');
    }
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentEndPoint(null);
    setSelectedTool(null);
    setAnnotations([]);
    setDefects([]);
    setRecommendation('');
    setBlurredImageUrl(null);
    setDetectionResults([]);
    setScale(1);
    setOffset({ x: 0, y: 0 });
    setTimeout(() => redrawAnnotations(), 0);
  };

  const navigateToImage = (nextIndex) => {
    if (!onNavigateImage) return;
    resetForImageNavigation();
    onNavigateImage(nextIndex);
  };

  const handleSave = async () => {
    try {
      // Persist level info to DB if changed and image has id
      if (image?.id && typeof levelInfo === 'string' && levelInfo !== (image.floor_name || '')) {
        try {
          await apiService.updateImageLevel(image.id, levelInfo);
          try {
            window.dispatchEvent(new CustomEvent('pfi-image-level-updated', { detail: { imageId: image.id, floor_name: levelInfo } }));
          } catch {}
        } catch (e) {
          console.error('Failed to update image level:', e);
        }
      }
      // Save site plan annotations (right panel) unless we are in cover-only mode.
      if (!isCoverElevationAnnotationImage) {
        await persistSitePlanAnnotations();
      }

      if (isSimpleDrawingMode) {
        await persistSimpleDrawingDraft();

        onSave?.({ annotations, imageId: image?.id });
      setSnackbarMessage('Annotations saved');
      setSnackbarOpen(true);
      if (image?.id) {
        try {
          window.dispatchEvent(new CustomEvent('pfi-annotations-updated', { detail: { imageId: image.id } }));
        } catch {}
      }
      if (autoAdvanceOnSave && canNavigateNext && onNavigateImage) {
        navigateToImage(currentImageIndex + 1);
      }
        return;
      }
      const imageLevelDefects = normalizeDefects(defects);
      const savedDefects = allDefects;
      setDefects(imageLevelDefects);
      await syncImageDefectsToDatabase();
      
      const imageKey = getImageKey(image);
      if (imageKey) {
        try {
          localStorage.setItem(imageKey, JSON.stringify(annotations));
          localStorage.setItem(`${imageKey}_data`, JSON.stringify({
            annotations,
            defects: imageLevelDefects,
            recommendation
          }));
        } catch (e) {}
      }
      
      // Also call the original onSave callback for local state updates
      onSave({
        annotations,
        defects: savedDefects,
        recommendation,
        imageId: image?.id
      });
      setSnackbarMessage('Annotations saved');
      setSnackbarOpen(true);
      if (image?.id) {
        try {
          window.dispatchEvent(new CustomEvent('pfi-annotations-updated', { detail: { imageId: image.id } }));
        } catch {}
      }
      if (autoAdvanceOnSave && canNavigateNext && onNavigateImage) {
        navigateToImage(currentImageIndex + 1);
      }
    } catch (error) {
      console.error('Error saving annotation:', error);
      // Still call the original onSave for local fallback
      onSave({
        annotations,
        defects: allDefects,
        recommendation,
        imageId: image?.id
      });
      setSnackbarMessage('Failed to save annotation');
      setSnackbarOpen(true);
    }
  };

  const handleSaveLevelOnly = async () => {
    if (!image?.id) {
      setSnackbarMessage('No image selected');
      setSnackbarOpen(true);
      return;
    }
    if (levelInfo === (image.floor_name || '')) {
      setSnackbarMessage('No changes to level');
      setSnackbarOpen(true);
      return;
    }
    try {
      setIsSavingLevel(true);
      await apiService.updateImageLevel(image.id, levelInfo);
      try {
        window.dispatchEvent(new CustomEvent('pfi-image-level-updated', { detail: { imageId: image.id, floor_name: levelInfo } }));
      } catch {}
      setSnackbarMessage('Level saved');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('Save level failed:', e);
      setSnackbarMessage('Failed to save level');
      setSnackbarOpen(true);
    } finally {
      setIsSavingLevel(false);
    }
  };

  const handleReportGenerate = () => {
    // TODO: Implement report generation logic
    console.log('Generating report in format:', reportFormat);
    setShowReportConfirmation(false);
    onClose();
  };

  const handleReportPreview = () => {
    // TODO: Implement report preview logic
    console.log('Previewing report in format:', reportFormat);
  };

  const handleReportCancel = () => {
    setShowReportConfirmation(false);
    onClose();
  };

  const clearAnnotations = async () => {
    setAnnotations([]);
    setDefects([]);
    setRecommendation('');
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentEndPoint(null);
    
    // Also remove all data from localStorage
    if (image) {
      const imageKey = getImageKey(image);
      if (imageKey) {
        try {
          localStorage.removeItem(imageKey);
          localStorage.removeItem(`${imageKey}_data`);
          console.log('All annotation data cleared from localStorage for key:', imageKey);
        } catch (error) {
          console.error('Error clearing annotation data from localStorage:', error);
        }
      }
    }

    if (isSimpleDrawingMode) {
      try {
        await persistSimpleDrawingDraft([]);
        onSave?.({ annotations: [], imageId: image?.id });
      } catch (error) {
        console.error('Error clearing persisted simple annotations:', error);
      }
    }
    
    redrawAnnotations();
  };

  // Navigation functions
  const handlePreviousImage = () => {
    if (currentImageIndex > 0 && onNavigateImage) {
      navigateToImage(currentImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < images.length - 1 && onNavigateImage) {
      navigateToImage(currentImageIndex + 1);
    }
  };

  const canNavigatePrevious = images.length > 1 && currentImageIndex > 0;
  const canNavigateNext = images.length > 1 && currentImageIndex < images.length - 1;

  return (
    <>
      <Dialog
        open={Boolean(open)}
        onClose={handleDismiss}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6">Annotate Image</Typography>
              {images.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  Image {currentPhotoNumber} of {totalPhotoNumber}
                </Typography>
              )}
            </Box>
            <Box>
              {!isEditingLevelInline ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ cursor: 'pointer' }}
                  onDoubleClick={() => setIsEditingLevelInline(true)}
                  title="Double-click to edit level name"
                >
                  Level Name: {levelInfo || image?.floor_name || ''}
                </Typography>
              ) : (
                <TextField
                  size="small"
                  placeholder="e.g., L1, Level 2, Roof"
                  value={levelInfo}
                  autoFocus
                  onChange={(e) => setLevelInfo(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      await handleSaveLevelOnly();
                      setIsEditingLevelInline(false);
                    } else if (e.key === 'Escape') {
                      setIsEditingLevelInline(false);
                      setLevelInfo(image?.floor_name || '');
                    }
                  }}
                  onBlur={async () => {
                    await handleSaveLevelOnly();
                    setIsEditingLevelInline(false);
                  }}
                  sx={{ minWidth: 200 }}
                />
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {images.length > 1 && (
              <>
                <IconButton 
                onClick={handlePreviousImage} 
                disabled={Boolean(!canNavigatePrevious)}
                title="Previous Image"
              >
                <ArrowBackIcon />
              </IconButton>
              <IconButton 
                onClick={handleNextImage} 
                disabled={Boolean(!canNavigateNext)}
                title="Next Image"
              >
                  <ArrowForwardIcon />
                </IconButton>
              </>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <AnnotationContainer>
          <Box sx={{ position: 'relative', height: '100%', transition: 'width 0.3s ease' }}>
            <ToolsPanel sx={{
              width: isToolsPanelOpen ? 300 : 0,
              minWidth: isToolsPanelOpen ? 300 : 0,
              padding: isToolsPanelOpen ? 1 : 0,
              transition: 'all 0.3s ease',
              opacity: isToolsPanelOpen ? 1 : 0,
              overflowX: 'hidden',
              overflowY: isToolsPanelOpen ? 'auto' : 'hidden',
              border: isToolsPanelOpen ? undefined : 'none',
              whiteSpace: 'nowrap'
            }}>
            <Typography variant="subtitle1" fontWeight="bold">
              ANNOTATION TOOLS
            </Typography>
            
            <Stack direction="row" spacing={1}>
              <ToolButton
                active={!selectedTool}
                onClick={() => setSelectedTool(null)}
                title="Select"
              >
                <MouseIcon />
              </ToolButton>
              <ToolButton
                active={selectedTool === 'square'}
                onClick={() => setSelectedTool('square')}
                title="Square"
              >
                <CropFreeIcon />
              </ToolButton>
              {!isCoverElevationAnnotationImage && (
                <ToolButton
                  active={selectedTool === 'line'}
                  onClick={() => setSelectedTool('line')}
                  title="Line"
                >
                  <TimelineIcon />
                </ToolButton>
              )}
              {!isCoverElevationAnnotationImage && (
                <ToolButton
                  active={selectedTool === 'arrow'}
                  onClick={() => setSelectedTool('arrow')}
                  title="Double Arrow"
                >
                  <CompareArrowsIcon />
                </ToolButton>
              )}
              {!isCoverElevationAnnotationImage && (
                <ToolButton
                  active={selectedTool === 'text'}
                  onClick={() => setSelectedTool('text')}
                  title="Text"
                >
                  <TextFieldsIcon />
                </ToolButton>
              )}
            </Stack>
            {!isCoverElevationAnnotationImage && (
              <TextField size="small" label="Label Text" placeholder="Enter label and click on image" value={labelText} onChange={(e) => setLabelText(e.target.value)} />
            )}
            
            <Button variant="outlined" onClick={clearAnnotations} size="small">
              Clear All
            </Button>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoAdvanceOnSave}
                  onChange={(e) => setAutoAdvanceOnSave(Boolean(e.target.checked))}
                  size="small"
                  color="primary"
                />
              }
              label="Auto-advance after Save"
            />
            
            
            
            {false && (
            <Box display="flex" flexDirection="column" gap={1}>
              <FormControl fullWidth size="small">
                <InputLabel>Confidence Threshold</InputLabel>
                <Select
                  value={confidenceThreshold}
                  onChange={(e) => setConfidenceThreshold(e.target.value)}
                  label="Confidence Threshold"
                >
                  <MenuItem value={0.3}>30%</MenuItem>
                  <MenuItem value={0.5}>50%</MenuItem>
                  <MenuItem value={0.7}>70%</MenuItem>
                  <MenuItem value={0.9}>90%</MenuItem>
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(blurCars)}
                    onChange={(e) => setBlurCars(Boolean(e.target.checked))}
                    size="small"
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" fontWeight={blurCars ? "bold" : "normal"}>
                      🔵 Blur detected cars
                    </Typography>
                    {blurCars && (
                      <Chip 
                        label="ENABLED" 
                        size="small" 
                        color="primary" 
                        variant="filled"
                        sx={{ fontSize: '0.65rem', height: '18px' }}
                      />
                    )}
                  </Box>
                }
                sx={{ 
                  fontSize: '0.875rem',
                  backgroundColor: blurCars ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  borderRadius: 1,
                  padding: '4px 8px',
                  margin: '4px 0',
                  border: blurCars ? '1px solid rgba(25, 118, 210, 0.3)' : '1px solid transparent'
                }}
              />
              
                <Button
                  variant="contained"
                  onClick={handleYOLODetection}
                  disabled={Boolean(isDetecting || !isYoloEligibleImage)}
                startIcon={isDetecting ? <CircularProgress size={16} /> : <SmartToyIcon />}
                size="small"
                color="primary"
                sx={{
                  backgroundColor: blurCars ? '#1976d2' : undefined,
                  '&:hover': {
                    backgroundColor: blurCars ? '#1565c0' : undefined,
                  },
                  boxShadow: blurCars ? '0 2px 8px rgba(25, 118, 210, 0.3)' : undefined,
                }}
              >
                {isDetecting 
                  ? (blurCars ? 'Detecting & Blurring...' : 'Detecting...') 
                  : (blurCars ? '🔵 Detect & Blur Cars' : 'Detect Objects')
                }
              </Button>
              
              {detectionResults.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Found {detectionResults.length} objects
                </Typography>
              )}
              
              {blurredImageUrl && (
                <Box sx={{ 
                  mt: 1, 
                  p: 2, 
                  backgroundColor: 'rgba(76, 175, 80, 0.1)', 
                  borderRadius: 2,
                  border: '1px solid rgba(76, 175, 80, 0.3)'
                }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                      ✅ Blurred Image Created Successfully!
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Cars have been automatically blurred in the image below. You can toggle between original and blurred versions.
                  </Typography>
                  <Box mt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => {
                        // Toggle between original and blurred image
                        const currentSrc = imageRef.current?.src;
                        if (currentSrc && currentSrc.includes('_blurred')) {
                          // Currently showing blurred, switch to original
                          imageRef.current.src = annotationImageSrc;
                        } else {
                          // Currently showing original, switch to blurred
                          imageRef.current.src = blurredImageUrl;
                        }
                      }}
                      sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                    >
                      Toggle Original/Blurred
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            )}
            
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              ZOOM CONTROLS
            </Typography>
            
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={() => setScale(prev => Math.min(5, prev * 1.2))}
                size="small"
                title="Zoom In"
              >
                <AddIcon />
              </IconButton>
              <IconButton
                onClick={() => setScale(prev => Math.max(0.1, prev * 0.8))}
                size="small"
                title="Zoom Out"
              >
                <RemoveIcon />
              </IconButton>
              <Button
                variant="outlined"
                onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }}
                size="small"
              >
                Reset
              </Button>
            </Stack>
            
            {!isSimpleDrawingMode && (
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                STATUS
              </Typography>
            )}
            
            {!isSimpleDrawingMode && (
              <Typography variant="subtitle1" fontWeight="bold">
                Defects
              </Typography>
            )}
            
            {!isSimpleDrawingMode && (
            <Box display="flex" gap={1}>
              <FormControl fullWidth size="small">
                <Select
                  value={newDefect}
                  onChange={(e) => setNewDefect(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Select defect type</MenuItem>
                  {defectTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton onClick={addDefect} size="small">
                <AddIcon />
              </IconButton>
            </Box>
            )}
            
            {!isSimpleDrawingMode && hasMultipleDefectBoxes && targetDefectBox && (
              <Typography variant="caption" color="text.secondary">
                Adding defects to Box {targetDefectBoxNumber}
              </Typography>
            )}

            {!isSimpleDrawingMode && (
            <Stack
              spacing={1}
              // With several boxes, only this list shrinks/scrolls so the defect selector and recommendation stay visible
              sx={hasMultipleDefectBoxes ? { flex: '0 1 auto', minHeight: 120, overflow: 'hidden' } : undefined}
            >
              {hasMultipleDefectBoxes ? (
                <Box
                  ref={defectBoxListRef}
                  sx={{ position: 'relative', flex: '1 1 auto', minHeight: 0, maxHeight: 260, overflowY: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                {defectBoxes.map((box, boxIndex) => {
                  const boxDefects = getAnnotationDefects(box);
                  const isTarget = targetDefectBox && box.id === targetDefectBox.id;
                  return (
                    <Box
                      key={box.id}
                      data-defect-box-id={box.id}
                      onClick={() => setSelectedAnnotationId(box.id)}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: isTarget ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold" color={isTarget ? 'primary' : 'text.primary'}>
                        Box {boxIndex + 1}
                      </Typography>
                      {boxDefects.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">No defect selected</Typography>
                      ) : (
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {boxDefects.map((defectType, defectIndex) => (
                            <Chip
                              key={`${box.id}-${defectType}`}
                              label={defectType}
                              onDelete={() => removeBoxDefect(box.id, defectIndex)}
                              deleteIcon={<RemoveIcon />}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                })}
                </Box>
              ) : (
                getAnnotationDefects(targetDefectBox).map((defectType, defectIndex) => (
                  <Chip
                    key={`box-${defectType}`}
                    label={defectType}
                    onDelete={() => removeBoxDefect(targetDefectBox.id, defectIndex)}
                    deleteIcon={<RemoveIcon />}
                    size="small"
                    variant="outlined"
                  />
                ))
              )}
              {defects.map((defect, index) => (
                <Chip
                  key={index}
                  label={`${defect?.type || 'Unknown Defect'}${defect?.count > 1 ? ` (${defect.count})` : ''}`}
                  onDelete={() => removeDefect(index)}
                  deleteIcon={<RemoveIcon />}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
            )}
            
            {!isSimpleDrawingMode && (
              <Typography variant="subtitle1" fontWeight="bold">
                Recommendation
              </Typography>
            )}
            
            {!isSimpleDrawingMode && (
            <FormControl fullWidth size="small">
              <Select
                value={recommendation}
                onChange={(e) => {
                  const newRecommendation = e.target.value;
                  setRecommendation(newRecommendation);
                  
                  // Save recommendation to localStorage
                  if (image) {
                    const imageKey = getImageKey(image);
                    if (imageKey) {
                      try {
                        const savedData = localStorage.getItem(imageKey);
                        const existingAnnotations = savedData ? JSON.parse(savedData) : [];
                        const annotationData = {
                          annotations: existingAnnotations,
                          defects,
                          recommendation: newRecommendation
                        };
                        localStorage.setItem(`${imageKey}_data`, JSON.stringify(annotationData));
                        console.log('Recommendation saved to localStorage for key:', `${imageKey}_data`);
                      } catch (error) {
                        console.error('Error saving recommendation to localStorage:', error);
                      }
                    }
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  const value = String(selected || '').trim();
                  return value || 'Select recommendation';
                }}
              >
                <MenuItem value="">Select recommendation</MenuItem>
                {visibleRecommendationTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            )}
            
            {/* {annotations.length > 0 && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                  ANNOTATIONS ({annotations.length})
                </Typography>
                
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {annotations.map((annotation, index) => (
                    <Box
                      key={annotation.id}
                      sx={{
                        p: 1,
                        mb: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        fontSize: '0.75rem'
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="caption" fontWeight="bold">
                            {annotation.type.toUpperCase()} #{index + 1}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {annotation.type === 'square' && `${Math.round(annotation.width)}×${Math.round(annotation.height)}`}
                            {annotation.type === 'line' && 'Line annotation'}
                            {annotation.type === 'arrow' && 'Double arrow annotation'}
                            {annotation.type === 'text' && annotation.text}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedAnnotations = annotations.filter(a => a.id !== annotation.id);
                            setAnnotations(updatedAnnotations);
                            
                            // Save updated annotations to localStorage
                            if (image) {
                              const imageKey = getImageKey(image);
                              if (imageKey) {
                                try {
                                  localStorage.setItem(imageKey, JSON.stringify(updatedAnnotations));
                                  console.log('Annotation deleted and saved to localStorage for key:', imageKey);
                                } catch (error) {
                                  console.error('Error saving updated annotations to localStorage:', error);
                                }
                              }
                            }
                          }}
                          title="Delete"
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </>
            )} */}
          </ToolsPanel>
            <IconButton
              onClick={() => setIsToolsPanelOpen(!isToolsPanelOpen)}
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: -12,
                zIndex: 10,
                backgroundColor: 'background.paper',
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider',
                width: 24,
                height: 24,
                '&:hover': { backgroundColor: 'grey.100' }
              }}
            >
              {isToolsPanelOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Box>
          
          <ImageCanvas
            ref={containerRef}
            sx={{
              p: imageType === 'tapping' ? 1 : 0,
              alignItems: imageType === 'tapping' ? 'flex-start' : 'center',
              justifyContent: 'center',
            }}
          >
            {image && (
              <>
                <AnnotationImage
                  key={annotationImageKey}
                  ref={imageRef}
                  src={annotationImageSrc}
                  alt="Annotation target"
                  portrait={isPortrait}
                  imageType={imageType}
                  style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transformOrigin: '0 0' }}
                />
                <CanvasOverlay
                  ref={canvasRef}
                  width={800}
                  height={600}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  style={{ cursor: selectedTool ? 'crosshair' : 'default' }}
                />
                {selectedAnnotationId && (() => {
                  // Position action bar near selected annotation
                  const img = imageRef.current;
                  let pos = { left: 10, top: 10 };
                  if (img) {
                    const imgRect = img.getBoundingClientRect();
                    const natW = img.naturalWidth || 1;
                    const natH = img.naturalHeight || 1;
                    const sx = imgRect.width / natW;
                    const sy = imgRect.height / natH;
                    const ann = annotations.find(a => a.id === selectedAnnotationId);
                    if (ann) {
                      if (ann.type === 'square') {
                        const x = ann.x * sx + offset.x;
                        const y = ann.y * sy + offset.y;
                        pos = { left: x + 4, top: y - 28 };
                      } else if (ann.type === 'text') {
                        const x = ann.x * sx + offset.x;
                        const y = ann.y * sy + offset.y;
                        pos = { left: x + 4, top: y - 28 };
                      } else if (isLineLikeAnnotation(ann)) {
                        const x = Math.min(ann.startX, ann.endX) * sx + offset.x;
                        const y = Math.min(ann.startY, ann.endY) * sy + offset.y;
                        pos = { left: x + 4, top: y - 28 };
                      }
                    }
                  }
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: pos.left,
                        top: pos.top,
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        boxShadow: 2,
                        p: 0.5,
                        display: 'flex',
                        gap: 0.5,
                        zIndex: 12
                      }}
                    >
                      {!isCoverElevationAnnotationImage && (
                        <IconButton size="small" onClick={beginEditSelectedAnnotation} title="Edit label">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {!isCoverElevationAnnotationImage && (
                        <IconButton size="small" onClick={rotateSelectedAnnotation} title="Rotate 90°">
                          <Rotate90DegreesCwIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={deleteSelectedAnnotation} title="Delete">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })()}
                {!isCoverElevationAnnotationImage && isEditingAnnotation && selectedAnnotationId && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      right: 8,
                      display: 'flex',
                      gap: 1,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      borderRadius: 1,
                      p: 1,
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      label="Annotation text"
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyEditSelectedAnnotation();
                        } else if (e.key === 'Escape') {
                          setIsEditingAnnotation(false);
                        }
                      }}
                    />
                    <Button onClick={applyEditSelectedAnnotation} variant="contained" size="small">
                      Apply
                    </Button>
                    <Button onClick={() => setIsEditingAnnotation(false)} variant="outlined" size="small">
                      Cancel
                    </Button>
                  </Box>
                )}
              </>
            )}
          </ImageCanvas>

          {showSitePlanPanel && !isCoverElevationAnnotationImage && (
          <Box
            sx={{
              width: rightPanelWidth,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              overflow: 'hidden',
              position: 'relative',
              flexShrink: 0
            }}
          >


            {/* Resize Handle */}
            <Box
              onMouseDown={startResizingRight}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: 'col-resize',
                zIndex: 10,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.3)',
                }
              }}
            />

            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Color Legend
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  cursor: 'pointer',
                  opacity: annotationSeverity === 'repair' ? 1 : 0.6,
                }}
                onClick={() => setAnnotationSeverity('repair')}
              >
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    backgroundColor: '#FF9800',
                    border: '1px solid rgba(0,0,0,0.2)',
                  }}
                />
                <Typography variant="caption">
                  Requires repair
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  cursor: 'pointer',
                  opacity: annotationSeverity === 'unsafe' ? 1 : 0.6,
                }}
                onClick={() => setAnnotationSeverity('unsafe')}
              >
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    backgroundColor: '#F44336',
                    border: '1px solid rgba(0,0,0,0.2)',
                  }}
                />
                <Typography variant="caption">
                  Unsafe
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Site Plan
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={isSavingSitePlan ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveSitePlan}
                  disabled={isSavingSitePlan}
                  sx={{ 
                    minWidth: 'auto', 
                    px: 1, 
                    mr: 1,
                    fontSize: '0.75rem',
                    textTransform: 'none'
                  }}
                >
                  Save
                </Button>
                <IconButton
                  size="small"
                  onClick={() => setIsSitePlanModalOpen(true)}
                  title="Expand Site Plan"
                >
                  <FullscreenIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handlePrevSitePlan}
                  disabled={!canPrevSitePlan}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleNextSitePlan}
                  disabled={!canNextSitePlan}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1,
              }}
            >
              {currentSitePlanUrl ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    gap: 1,
                  }}
                >
                  <Box
                    ref={sitePlanContainerRef}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: 'auto',
                      maxHeight: '100%',
                      aspectRatio: sitePlanAspectRatio || 1,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: isSitePlanModalOpen ? 'crosshair' : 'zoom-in',
                    }}
                    onMouseDown={handleSitePlanMouseDown}
                  >
                    <Box
                      component="img"
                      src={currentSitePlanUrl}
                      alt="Site plan image"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                    {currentSitePlanAnnotations.map((ann) => {
                      const isSelected = selectedSitePlanAnnotationId === ann.id;
                      const label = resolveSitePlanLabel(ann);
                      return (
                      <Box
                        key={ann.id}
                        onMouseDown={(e) => {
                           e.stopPropagation();
                           setSelectedSitePlanAnnotationId(ann.id);
                           handleResizeStart(e, 'move', ann);
                         }}
                        sx={{
                          position: 'absolute',
                          left: `${ann.x * 100}%`,
                          top: `${ann.y * 100}%`,
                          width: `${ann.width * 100}%`,
                          height: `${ann.height * 100}%`,
                          borderRadius: 0,
                          backgroundColor: ann.severity === 'unsafe' ? 'rgba(244, 67, 54, 0.9)' : '#FFB300',
                          border: '2px solid #111',
                          boxShadow: isSelected ? '0 0 0 2px rgba(255,255,255,0.95)' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          color: '#000',
                          padding: '2px 4px',
                          boxSizing: 'border-box',
                          cursor: 'move',
                          zIndex: isSelected ? 10 : 1,
                          pointerEvents: 'auto',
                        }}
                      >
                        <Box sx={{ fontSize: `${getPlanFontPx(sitePlanContainerRef, ann, label)}px`, fontWeight: 700, textAlign: 'center', lineHeight: 1, letterSpacing: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'clip', userSelect: 'none', pointerEvents: 'none', maxWidth: '100%' }}>
                          {label}
                        </Box>
                        {isSelected ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSitePlanAnnotation(ann.id);
                              }}
                              sx={{ position: 'absolute', top: -18, right: -18, width: 20, height: 20, backgroundColor: 'white', boxShadow: 1 }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            {['nw', 'ne', 'sw', 'se'].map((handle) => (
                              <Box
                                key={handle}
                                onMouseDown={(e) => handleResizeStart(e, handle, ann)}
                                sx={{
                                  position: 'absolute',
                                  width: 8,
                                  height: 8,
                                  backgroundColor: 'white',
                                  border: '1px solid #000',
                                  cursor: `${handle}-resize`,
                                  top: handle.includes('n') ? -4 : 'auto',
                                  bottom: handle.includes('s') ? -4 : 'auto',
                                  left: handle.includes('w') ? -4 : 'auto',
                                  right: handle.includes('e') ? -4 : 'auto',
                                  zIndex: 11,
                                }}
                              />
                            ))}
                          </>
                        ) : null}
                      </Box>
                      );
                    })}
                    {sitePlanCurrentRect && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${Math.min(sitePlanCurrentRect.x1, sitePlanCurrentRect.x2) * 100}%`,
                          top: `${Math.min(sitePlanCurrentRect.y1, sitePlanCurrentRect.y2) * 100}%`,
                          width: `${Math.abs(sitePlanCurrentRect.x2 - sitePlanCurrentRect.x1) * 100}%`,
                          height: `${Math.abs(sitePlanCurrentRect.y2 - sitePlanCurrentRect.y1) * 100}%`,
                          border: annotationSeverity === 'unsafe' ? '2px dashed #F44336' : '2px dashed #FF9800',
                          boxSizing: 'border-box',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </Box>
                  {onChangeSitePlan && (
                    <Button
                      variant={sitePlanImageUrl === currentSitePlanUrl ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => onChangeSitePlan(currentSitePlanUrl)}
                    >
                      {sitePlanImageUrl === currentSitePlanUrl ? 'Selected site plan' : 'Use this site plan'}
                    </Button>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  Section 1.2 plan image is not uploaded yet. Please upload a plan in
                  {' '}
                  1.2 Overview of building elevations.
                </Typography>
              )}
            </Box>
          </Box>
          )}
        </AnnotationContainer>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleDismiss}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSimpleDrawingMode ? false : Boolean(allDefects.length === 0 || !recommendation)}
        >
          Save Annotation
        </Button>
      </DialogActions>
    </Dialog>

    {/* Report Confirmation Dialog */}
    <Dialog
      open={Boolean(showReportConfirmation)}
      onClose={handleReportCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'primary.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="h4">📄</Typography>
          </Box>
          <Typography variant="h6">Confirm Report Generation</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          You are about to generate a new report for 'Project North Star'. Please confirm your selection.
        </Typography>
        
        <FormControl fullWidth>
          <InputLabel>Report Format</InputLabel>
          <Select
            value={reportFormat}
            onChange={(e) => setReportFormat(e.target.value)}
            label="Report Format"
          >
            <MenuItem value="PDF">PDF</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleReportCancel}
          variant="outlined"
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleReportPreview}
          variant="outlined"
          startIcon={<Typography>👁</Typography>}
        >
          Preview Report
        </Button>
        <Button
          onClick={handleReportGenerate}
          variant="contained"
          startIcon={<Typography>⚡</Typography>}
        >
          Generate Report
        </Button>
      </DialogActions>
     </Dialog>

    {/* Site Plan Modal */}
    <Dialog
      open={isSitePlanModalOpen}
      onClose={() => setIsSitePlanModalOpen(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Annotate Site Plan</Typography>
          <IconButton onClick={() => setIsSitePlanModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ height: '80vh', p: 1 }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          {currentSitePlanUrl ? (
            <Box
              ref={sitePlanModalRef}
              sx={{
                position: 'relative',
                width: '100%',
                height: 'auto',
                maxHeight: '100%',
                aspectRatio: sitePlanAspectRatio || 1,
                alignSelf: 'center',
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'crosshair',
                border: '1px solid',
                borderColor: 'divider',
              }}
              onMouseDown={handleSitePlanMouseDown}
            >
              <Box
                component="img"
                src={currentSitePlanUrl}
                alt="Site plan image"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
              {currentSitePlanAnnotations.map((ann) => {
                const isSelected = selectedSitePlanAnnotationId === ann.id;
                const label = resolveSitePlanLabel(ann);
                return (
                <Box
                  key={ann.id}
                  onMouseDown={(e) => {
                     e.stopPropagation();
                     setSelectedSitePlanAnnotationId(ann.id);
                     handleResizeStart(e, 'move', ann);
                   }}
                  sx={{
                    position: 'absolute',
                    left: `${ann.x * 100}%`,
                    top: `${ann.y * 100}%`,
                    width: `${ann.width * 100}%`,
                    height: `${ann.height * 100}%`,
                    borderRadius: 0,
                    backgroundColor: ann.severity === 'unsafe' ? 'rgba(244, 67, 54, 0.9)' : '#FFB300',
                    border: '2px solid #111',
                    boxShadow: isSelected ? '0 0 0 2px rgba(255,255,255,0.95)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    color: '#000',
                    padding: '2px 4px',
                    boxSizing: 'border-box',
                    cursor: 'move',
                    zIndex: isSelected ? 10 : 1,
                    pointerEvents: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: `${getPlanFontPx(sitePlanModalRef, ann, label)}px`,
                      fontWeight: 700,
                      textAlign: 'center',
                      lineHeight: 1,
                      letterSpacing: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'clip',
                      userSelect: 'none',
                      pointerEvents: 'none',
                      maxWidth: '100%'
                    }}
                  >
                    {label}
                  </Box>
                  {isSelected ? (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSitePlanAnnotation(ann.id);
                        }}
                        sx={{ position: 'absolute', top: -18, right: -18, width: 20, height: 20, backgroundColor: 'white', boxShadow: 1 }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      {['nw', 'ne', 'sw', 'se'].map((handle) => (
                        <Box
                          key={handle}
                          onMouseDown={(e) => handleResizeStart(e, handle, ann)}
                          sx={{
                            position: 'absolute',
                            width: 8,
                            height: 8,
                            backgroundColor: 'white',
                            border: '1px solid #000',
                            cursor: `${handle}-resize`,
                            top: handle.includes('n') ? -4 : 'auto',
                            bottom: handle.includes('s') ? -4 : 'auto',
                            left: handle.includes('w') ? -4 : 'auto',
                            right: handle.includes('e') ? -4 : 'auto',
                            zIndex: 11,
                          }}
                        />
                      ))}
                    </>
                  ) : null}
                </Box>
                );
              })}
              {sitePlanCurrentRect && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${Math.min(sitePlanCurrentRect.x1, sitePlanCurrentRect.x2) * 100}%`,
                    top: `${Math.min(sitePlanCurrentRect.y1, sitePlanCurrentRect.y2) * 100}%`,
                    width: `${Math.abs(sitePlanCurrentRect.x2 - sitePlanCurrentRect.x1) * 100}%`,
                    height: `${Math.abs(sitePlanCurrentRect.y2 - sitePlanCurrentRect.y1) * 100}%`,
                    border: annotationSeverity === 'unsafe' ? '2px dashed #F44336' : '2px dashed #FF9800',
                    boxSizing: 'border-box',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Box>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
               <Typography>No site plan available</Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="center">
            {onChangeSitePlan && currentSitePlanUrl && (
                <Button
                  variant={sitePlanImageUrl === currentSitePlanUrl ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => onChangeSitePlan(currentSitePlanUrl)}
                >
                  {sitePlanImageUrl === currentSitePlanUrl ? 'Selected site plan' : 'Use this site plan'}
                </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsSitePlanModalOpen(false)}>Done</Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleSnackbarClose} severity={snackbarMessage.includes('success') ? 'success' : 'error'} sx={{ width: '100%' }}>
        {snackbarMessage}
      </Alert>
    </Snackbar>
   </>
 );
 };

export default ImageAnnotationModal;
